import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  House,
  Users,
  QrCode,
  UserCircle,
  SignOut,
  Copy,
  ShareNetwork,
  LinkSimple,
  Info,
  X,
  Coins,
  UserPlus,
  Plus,
  UsersThree,
  SealCheck,
  Clock,
  Phone,
  EnvelopeSimple,
  ArrowRight,
  Sun,
  Export,
  Briefcase,
  Heart,
  Ticket,
  Trash,
  Check,
  Key,
} from "@phosphor-icons/react";
import { Logo, NEUTRAL_NAME, DEFAULT_CLAIM } from "../components/Logo";
import { Paywall } from "./Paywall";
import { NotificationBell } from "../components/NotificationBell";
import { PushToggle } from "../components/PushToggle";
import { BottomNav } from "../components/BottomNav";
import { StatusPill } from "../components/StatusPill";
import { MailDraftDialog } from "../components/MailDraftDialog";
import { KarriereDialog } from "../components/KarriereDialog";
import { ConfirmDelete } from "../components/ConfirmDelete";
import { JournalDialog } from "../components/JournalDialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ImageCropDialog } from "../components/ImageCropDialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { api, apiError } from "../lib/api";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { copyText, shareLink, shareImage } from "../lib/clipboard";
import { useAuth } from "../context/AuthContext";

const STATUS_OPTIONS = [
  { value: "eingereicht", label: "Empfehlung eingegangen" },
  { value: "termin", label: "Termin stattgefunden" },
  { value: "in_bearbeitung", label: "In Ausarbeitung" },
  { value: "kunde_geworden", label: "Kunde", check: true },
  { value: "kein_kunde", label: "Kein Kunde", cross: true },
];

const NAV = [
  { key: "uebersicht", label: "Übersicht", icon: House },
  { key: "empfehlungen", label: "Empfehlungen", icon: Users },
  { key: "qr", label: "QR-Code", icon: QrCode },
  { key: "profil", label: "Profil", icon: UserCircle },
];

const POSTER_VARIANTS_PRAEMIE = [
  { key: "standard", label: "Klassisch" },
  { key: "praemie", label: "Prämie" },
  { key: "einladung", label: "Einladung" },
];
const POSTER_VARIANTS_NEUTRAL = [
  { key: "vertrauen", label: "Vertrauen" },
  { key: "netzwerk", label: "Netzwerk" },
  { key: "tipp", label: "Mein Tipp" },
];

const SLOGAN_OPTIONS = [
  { k: "praemie", t: "Empfehlen Sie uns weiter – wir bedanken uns." },
  { k: "ueberzeugung", t: "Empfehlen aus Überzeugung – weil gute Beratung Weiterempfehlung verdient." },
  { k: "beratung", t: "Kennst du jemanden, dem ich mit ehrlicher Beratung helfen kann?" },
  { k: "unternehmerlohn", t: "Eine Empfehlung ist das höchste Gut – der wahre Unternehmerlohn." },
  { k: "vertrauen", t: "Weiterempfehlung ist gelebtes Vertrauen." },
  { k: "netzwerk", t: "Dein Netzwerk ist dein wertvollstes Kapital." },
  { k: "gemeinsam", t: "Gemeinsam mehr erreichen – durch echte Empfehlungen." },
  { k: "wert", t: "Eine gute Empfehlung ist mehr wert als jede Werbung." },
  { k: "persoenlich", t: "Weil persönliche Empfehlungen Vertrauen schaffen." },
  { k: "zukunft", t: "Gemeinsam die finanzielle Zukunft gestalten." },
  { k: "danke", t: "Danke, dass Sie an mich denken." },
  { k: "kompass", t: "Ehrliche Beratung als Kompass für Ihre Finanzen." },
];

const CLAIM_OPTIONS = [
  "Empfehlen & Profitieren",
  "Weiterempfehlen aus Überzeugung",
  "Empfehlen lohnt sich",
  "Weiterempfehlung ist gelebtes Vertrauen",
  "Gute Beratung spricht sich herum",
  "Gemeinsam mehr erreichen",
];

const SUBTEXT_OPTIONS_PRAEMIE = [
  "Empfehlen Sie eine Person, die von einer professionellen Vermögensberatung profitiert. Verfolgen Sie den Fortschritt live und sichern Sie sich Ihre Prämie.",
  "Für jeden geworbenen Kunden gibt es eine attraktive Geldprämie – transparent und fair.",
  "Kennen Sie jemanden, der von ehrlicher Beratung profitiert? Empfehlen Sie weiter und sichern Sie sich Ihre Prämie.",
  "Sie kennen jemanden, der eine ehrliche Finanzberatung sucht? Empfehlen Sie weiter – als Dankeschön erhalten Sie Ihre Prämie.",
  "Gute Beratung lebt von Weiterempfehlung. Für jede erfolgreiche Empfehlung bedanke ich mich mit einer Prämie.",
];
const BRANCHEN_THEMEN = {
  finanz: [
    "Zahlen Sie noch unnötige Kontoführungsgebühren?",
    "Schritt für Schritt ein Vermögen fürs Alter aufbauen",
    "Der Weg in die eigenen vier Wände – früh das Fundament legen",
    "Früh an später denken – clevere Altersvorsorge",
    "Familie und Einkommen zuverlässig absichern",
    "Ihr Sparziel schneller erreichen",
    "Mehr aus Ihrem Ersparten machen",
    "Die eigene Arbeitskraft clever absichern",
    "Steuern clever nutzen – mehr netto vom Brutto",
    "Gesundheit rundum gut abgesichert",
    "Private Krankenversicherung – die beste Wahl für Sie?",
    "Steuervorteile voll ausschöpfen",
    "Keine staatliche Förderung verschenken",
    "Für die Kinder früh den Grundstein legen",
    "Versicherungs-Check: zahlen Sie zu viel oder fehlt Schutz?",
    "Vermögen aufbauen – mit Struktur und Strategie",
    "Clever investieren – Ihr Geld für sich arbeiten lassen",
  ],
  energie: [
    "Stromkosten spürbar und dauerhaft senken",
    "Unabhängig werden mit eigener Photovoltaik",
    "Heizkosten senken mit moderner Wärmepumpe",
    "Alle Förderungen optimal ausschöpfen",
    "Sonnenstrom speichern – Tag und Nacht nutzen",
    "Das E-Auto bequem zuhause laden",
  ],
  immobilien: [
    "Immobilie verkaufen – zum bestmöglichen Preis",
    "Die passende Immobilie finden – Ihr neues Zuhause",
    "Kauf und Finanzierung clever planen",
    "Anschlussfinanzierung rechtzeitig sichern",
    "Was ist Ihre Immobilie heute wert?",
    "Vermieten statt verkaufen – lohnt sich das?",
  ],
  auto: [
    "Ihr Wunschfahrzeug perfekt konfigurieren",
    "Finanzierung oder Leasing – clever vergleichen",
    "Ihr altes Auto fair in Zahlung geben",
    "Junge Gebrauchte – geprüft und mit Garantie",
    "Anschlussgarantie und Schutzbrief sichern",
    "Elektro oder Verbrenner – was passt zu Ihnen?",
  ],
};
const themenFor = (b) => BRANCHEN_THEMEN[b] || [];
const KARRIERE_TEXTE_BRANCHE = {
  finanz: ["Interesse am Beraterberuf?", "Werde Teil unseres Teams!", "Karriere mit Sinn – jetzt informieren", "Nebenberuflich einsteigen?", "Quereinsteiger willkommen", "Zweites Standbein aufbauen?"],
  energie: ["Werde Energieberater:in", "Werde Teil unseres Teams!", "Zukunftsbranche Energie – jetzt einsteigen", "Nebenberuflich in die Energiewende", "Quereinsteiger willkommen"],
  immobilien: ["Interesse am Immobilienberuf?", "Werde Teil unseres Maklerteams!", "Karriere in der Immobilienbranche", "Nebenberuflich als Immobilienprofi", "Quereinsteiger willkommen"],
  auto: ["Interesse an einer Karriere im Autohaus?", "Werde Teil unseres Teams!", "Karriere rund ums Auto", "Nebenberuflich durchstarten", "Quereinsteiger willkommen"],
  it: ["Interesse an einer Karriere in der IT?", "Werde Teil unseres Teams!", "Karriere in der IT-Branche", "Quereinsteiger willkommen"],
  dienstleistung: ["Interesse an einer Mitarbeit?", "Werde Teil unseres Teams!", "Karriere mit Sinn", "Quereinsteiger willkommen"],
  sonstige: ["Interesse an einer Mitarbeit?", "Werde Teil unseres Teams!", "Karriere mit Sinn", "Quereinsteiger willkommen"],
};
const karriereOptionsFor = (b) => {
  const br = KARRIERE_TEXTE_BRANCHE[b] ? b : "finanz";
  return KARRIERE_TEXTE_BRANCHE[br].map((t, i) => ({ k: `karr_${br}_${i}`, t }));
};
const sloganTextFor = (variante, branche) => {
  const base = SLOGAN_OPTIONS.find((o) => o.k === variante);
  if (base) return base.t;
  const m = /^thema_(.+)_(\d+)$/.exec(variante || "");
  if (m) { const list = BRANCHEN_THEMEN[m[1]] || []; return list[Number(m[2])] || ""; }
  return "";
};

const SUBTEXT_OPTIONS_NEUTRAL = [
  "Kennst du jemanden, der von einer ehrlichen, professionellen Vermögensberatung profitiert? Deine Empfehlung hilft weiter – ganz ohne Prämienversprechen.",
  "Empfehlen aus Überzeugung – weil gute Beratung Weiterempfehlung verdient.",
  "Eine gute Empfehlung ist gelebtes Vertrauen. Teile sie mit jemandem, dem sie weiterhilft.",
  "Wen kennst du, der von einer ehrlichen, unabhängigen Beratung profitieren würde? Eine kurze Empfehlung genügt.",
  "Vertrauen weitergeben ist das schönste Kompliment. Empfiehl mich weiter – ganz ohne Verpflichtung.",
];

const POSTER_HEADLINES = {
  standard: ["Jetzt scannen", "& empfehlen"],
  praemie: ["Empfehlen &", "profitieren"],
  einladung: ["Mein Tipp", "für dich"],
  vertrauen: ["Weiterempfehlen", "aus Überzeugung"],
  netzwerk: ["Kennst du", "jemanden?"],
  tipp: ["Mein Tipp", "für dich"],
};

const DONATE_PAYPAL_EMAIL = "steffenhermes@web.de";
const FEEDBACK_EMAIL = "webmaster@empfehlungs-kompass.de";
// Opens PayPal to send to the developer's email. In the PayPal app the payer can
// choose „Freunde und Bekannte" (Freunde & Familie) to avoid fees.
const DONATE_URL = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(DONATE_PAYPAL_EMAIL)}&item_name=${encodeURIComponent("Spende an Entwickler")}&currency_code=EUR`;
const LIZENZ_URL = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(DONATE_PAYPAL_EMAIL)}&item_name=${encodeURIComponent("Voll-Lizenz Prämio – dauerhafte Freischaltung")}&currency_code=EUR`;

export default function BeraterDashboard() {
  const { user, logout, refresh } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [auszahlungen, setAuszahlungen] = useState([]);
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("fall") ? "empfehlungen" : "uebersicht"));

  const load = useCallback(async () => {
    try {
      const [{ data: refs }, { data: pays }] = await Promise.all([
        api.get("/berater/referrals"),
        api.get("/berater/auszahlungen"),
      ]);
      setReferrals(refs);
      setAuszahlungen(pays);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/referrals/${id}/status`, { status });
      toast.success("Status aktualisiert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const doReward = async (id, action) => {
    try {
      const res = await api.post(`/referrals/${id}/${action}`);
      const nowBezahlt = res?.data?.praemie_bezahlt;
      toast.success(action === "freigeben" ? (praemieAktiv ? "Prämie freigegeben – Kunde informiert." : "Als erfolgreich markiert – Kunde informiert.") : (nowBezahlt ? "Als bezahlt markiert." : "Bezahlt-Markierung zurückgenommen."));
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const markAusgezahlt = async (id) => {
    try {
      await api.post(`/berater/auszahlungen/${id}/ausgezahlt`);
      toast.success("Als ausgezahlt markiert – Kunde informiert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const markAbgelehnt = async (id) => {
    const grund = window.prompt("Grund der Ablehnung (optional):") || "";
    try {
      await api.post(`/berater/auszahlungen/${id}/ablehnen`, { grund });
      toast.success("Auszahlung abgelehnt – Kunde informiert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const openCsv = async (jahr, fmt = "standard") => {
    try {
      const res = await api.get(`/berater/auszahlungen.csv`, { params: { jahr, format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "datev" ? `EXTF_Buchungsstapel_${jahr}.csv` : `auszahlungen-${jahr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const openJahresbeleg = async (jahr) => {
    try {
      const res = await api.get(`/berater/jahresbeleg.pdf`, { params: { jahr }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const openBeleg = async (id) => {
    try {
      const res = await api.get(`/berater/auszahlungen/${id}/beleg.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const mailBeleg = async (id) => {
    try {
      const { data } = await api.post(`/berater/auszahlungen/${id}/beleg-mail`);
      toast.success(`Steuer-Beleg per E-Mail an ${data.email} gesendet.`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteReferral = async (id) => {
    try {
      await api.delete(`/referrals/${id}`);
      toast.success("Fall gelöscht.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const bulkDeleteReferrals = async (ids) => {
    try {
      const { data } = await api.post(`/referrals/bulk-delete`, { ids });
      toast.success(`${data.deleted} Fälle gelöscht.`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const resendConfirm = async (id) => {
    try {
      await api.post(`/referrals/${id}/resend-confirm`);
      toast.success("Bestätigungs-Mail erneut gesendet.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const addPraemie = async (id, payload) => {
    try {
      await api.post(`/referrals/${id}/praemie-add`, payload || {});
      toast.success("Prämie hinzugefügt.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const removePraemie = async (id) => {
    try {
      await api.post(`/referrals/${id}/praemie-remove`);
      toast.success("Prämie entfernt.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const praemieAktiv = user?.praemie_aktiv !== false;
  const kundenGeworden = referrals.filter((r) => r.status === "kunde_geworden").length;
  const erfolgreich = referrals.filter((r) => r.praemie_freigeschaltet).length;
  const anyPraemie = praemieAktiv || referrals.some((r) => r.praemie_aktiv !== false);
  const offen = referrals.filter((r) => ["eingereicht", "in_bearbeitung", "termin"].includes(r.status)).length;
  const praemienOffen = referrals
    .filter((r) => r.praemie_freigeschaltet && !r.praemie_bezahlt)
    .reduce((s, r) => s + (r.praemie_betrag || 0), 0);
  const praemienBezahlt = referrals
    .filter((r) => r.praemie_bezahlt)
    .reduce((s, r) => s + (r.praemie_betrag || 0), 0);
  const jahrNow = new Date().getFullYear();
  const ausgezahltJahr = (auszahlungen || [])
    .filter((a) => a.status === "ausgezahlt" && a.ausgezahlt_am && new Date(a.ausgezahlt_am).getFullYear() === jahrNow)
    .reduce((s, a) => s + (a.betrag || 0), 0);
  const ausgezahltVorjahr = (auszahlungen || [])
    .filter((a) => a.status === "ausgezahlt" && a.ausgezahlt_am && new Date(a.ausgezahlt_am).getFullYear() === jahrNow - 1)
    .reduce((s, a) => s + (a.betrag || 0), 0);

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Abo-Portal konnte nicht geöffnet werden.");
    }
  };
  const fallParam = new URLSearchParams(window.location.search).get("fall");
  if (user?.rolle === "berater" && user?.billing && user.billing.zugang === false) return <Paywall />;

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#F8F9FA]" data-testid="berater-dashboard">
      <FirmaAdminOnboarding user={user} />
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-[#E2E8F0] bg-white/90 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] backdrop-blur-xl">
        <Logo {...(user?.praemie_aktiv === false ? { name: NEUTRAL_NAME, claim: user?.claim || DEFAULT_CLAIM } : { claim: user?.claim || "" })} />
        <NotificationBell />
      </header>

      <main className="px-4 pb-28 pt-4">
        {user?.billing?.warnung && (
          <div data-testid="zahlung-warnung" className="mb-4 rounded-2xl border border-[#DD6B20]/40 bg-[#FFFAF0] p-4">
            <p className="text-sm font-semibold text-[#9C4221]">Zahlung fehlgeschlagen</p>
            <p className="mt-1 text-xs leading-relaxed text-[#B7791F]">Deine letzte Kartenabbuchung (9,90 €) ist fehlgeschlagen. Bitte aktualisiere dein Zahlungsmittel, sonst wird dein Zugang in wenigen Tagen pausiert.</p>
            <button data-testid="zahlung-warnung-btn" onClick={openPortal} className="mt-3 h-10 w-full rounded-full bg-[#DD6B20] text-sm font-semibold text-white transition-all active:scale-[0.99]">Zahlungsmittel aktualisieren</button>
          </div>
        )}
        {tab === "uebersicht" && (
          <Uebersicht name={user?.name} praemieAktiv={anyPraemie} kundenGeworden={kundenGeworden} erfolgreich={erfolgreich} offen={offen} praemienOffen={praemienOffen} praemienBezahlt={praemienBezahlt} ausgezahltJahr={ausgezahltJahr} ausgezahltVorjahr={ausgezahltVorjahr} jahrNow={jahrNow} referrals={referrals} auszahlungen={auszahlungen} onAusgezahlt={markAusgezahlt} onAbgelehnt={markAbgelehnt} onBeleg={openBeleg} onBelegMail={mailBeleg} onJahresbeleg={openJahresbeleg} onCsv={openCsv} goList={() => setTab("empfehlungen")} />
        )}
        {tab === "empfehlungen" && <EmpfehlungenList referrals={referrals} highlightId={fallParam} praemieAktiv={praemieAktiv} defaultBetrag={user?.praemie_betrag || 50} changeStatus={changeStatus} onReward={doReward} onDelete={deleteReferral} onBulkDelete={bulkDeleteReferrals} onResend={resendConfirm} onAddPraemie={addPraemie} onRemovePraemie={removePraemie} onReload={load} />}
        {tab === "qr" && <QrPanel user={user} refresh={refresh} beraterCode={user?.berater_code} name={user?.name} praemie={user?.praemie_betrag || 0} praemieAktiv={praemieAktiv} slogan={user?.slogan_custom?.trim() || (SLOGAN_OPTIONS.find((o) => o.k === (user?.slogan_variante || (user?.praemie_aktiv !== false ? "praemie" : "vertrauen"))) || {}).t || ""} />}
        {tab === "profil" && <Profil user={user} refresh={refresh} logout={logout} />}
      </main>

      <BottomNav items={NAV} active={tab} onChange={setTab} />
    </div>
  );
}

function Uebersicht({ name, praemieAktiv, kundenGeworden, erfolgreich, offen, praemienOffen, praemienBezahlt, ausgezahltJahr, ausgezahltVorjahr, jahrNow, referrals, auszahlungen, onAusgezahlt, onAbgelehnt, onBeleg, onBelegMail, onJahresbeleg, onCsv, goList }) {
  const [belegJahr, setBelegJahr] = useState(jahrNow);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };
  const absagen = referrals.filter((r) => r.status === "kein_kunde");
  const absageGruende = (() => {
    const m = {};
    absagen.forEach((r) => {
      const key = (r.absage_grund || "").trim() || "Ohne Angabe";
      m[key] = (m[key] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();
  const themenStats = (() => {
    const m = {};
    referrals.forEach((r) => { const k = (r.anliegen || "").trim(); if (k) m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();
  const themenTotal = themenStats.reduce((s, [, c]) => s + c, 0);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="tab-uebersicht">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#0A1128]">Hallo, {name || "Berater"}</h1>
      {praemieAktiv ? (
      <>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F1] p-5 shadow-[0_4px_20px_rgba(197,160,89,0.10)]" data-testid="kpi-praemien">
          <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#C5A059]/20 blur-2xl" />
          <Coins size={26} weight="duotone" color="#C5A059" />
          <p className="mt-4 text-xs text-[#8a6d2f]">Offene Prämien</p>
          <p className="mt-1 text-4xl font-extrabold leading-none text-[#0A1128]">{praemienOffen} €</p>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-[#276749]/25 bg-[#276749]/5 p-5 shadow-[0_4px_20px_rgba(39,103,73,0.08)]" data-testid="kpi-ausgezahlt-jahr">
          <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#276749]/15 blur-2xl" />
          <SealCheck size={26} weight="duotone" color="#276749" />
          <p className="mt-4 text-xs text-[#276749]">Ausgezahlt {jahrNow}</p>
          <p className="mt-1 text-4xl font-extrabold leading-none text-[#0A1128]">{ausgezahltJahr} €</p>
          {(ausgezahltVorjahr > 0 || ausgezahltJahr > 0) && (
            <p className={`mt-2 text-[11px] font-semibold ${ausgezahltJahr - ausgezahltVorjahr >= 0 ? "text-[#276749]" : "text-[#C0392B]"}`} data-testid="kpi-vorjahr-delta">
              {ausgezahltJahr - ausgezahltVorjahr >= 0 ? "+" : ""}{ausgezahltJahr - ausgezahltVorjahr} € ggü. {jahrNow - 1}
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="flex items-center justify-between rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3.5" data-testid="kpi-offen">
          <div className="flex items-center gap-2"><UserPlus size={20} weight="duotone" color="#0A1128" /><span className="text-sm font-semibold text-[#4A5568]">Offene Empf.</span></div>
          <span className="text-2xl font-extrabold text-[#0A1128]">{offen}</span>
        </div>
        <div className="flex items-center justify-between rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3.5" data-testid="kpi-ausgezahlt">
          <div className="flex items-center gap-2"><SealCheck size={20} weight="fill" color="#276749" /><span className="text-sm font-semibold text-[#4A5568]">Gesamt</span></div>
          <span className="text-2xl font-extrabold text-[#276749]">{praemienBezahlt} €</span>
        </div>
      </div>
      </>
      ) : (
      <>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F1] p-5 shadow-[0_4px_20px_rgba(197,160,89,0.10)]" data-testid="kpi-kunden">
          <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#C5A059]/20 blur-2xl" />
          <UsersThree size={26} weight="duotone" color="#C5A059" />
          <p className="mt-4 text-xs text-[#8a6d2f]">Kunden geworden</p>
          <p className="mt-1 text-4xl font-extrabold leading-none text-[#0A1128]">{kundenGeworden}</p>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-[#276749]/25 bg-[#276749]/5 p-5 shadow-[0_4px_20px_rgba(39,103,73,0.08)]" data-testid="kpi-erfolgreich">
          <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#276749]/15 blur-2xl" />
          <SealCheck size={26} weight="duotone" color="#276749" />
          <p className="mt-4 text-xs text-[#276749]">Erfolgreich markiert</p>
          <p className="mt-1 text-4xl font-extrabold leading-none text-[#0A1128]">{erfolgreich}</p>
        </div>
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between rounded-2xl border border-[#E2E8F0] bg-white px-4 py-3.5" data-testid="kpi-offen">
          <div className="flex items-center gap-2"><UserPlus size={20} weight="duotone" color="#0A1128" /><span className="text-sm font-semibold text-[#4A5568]">Offene Empfehlungen</span></div>
          <span className="text-2xl font-extrabold text-[#0A1128]">{offen}</span>
        </div>
      </div>
      </>
      )}

      {praemieAktiv && auszahlungen && auszahlungen.length > 0 && (
        <div className="mt-8" data-testid="auszahlung-verlauf">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Auszahlungs-Verlauf</h2>
            <div className="flex items-center gap-1.5">
              <select data-testid="jahresbeleg-jahr" value={belegJahr} onChange={(e) => setBelegJahr(Number(e.target.value))} className="rounded-full border border-[#E2E8F0] bg-white px-2 py-1 text-[11px] font-semibold text-[#0A1128]">
                {[0, 1, 2, 3].map((n) => <option key={n} value={jahrNow - n}>{jahrNow - n}</option>)}
              </select>
              <button data-testid="jahresbeleg-btn" onClick={() => onJahresbeleg(belegJahr)} className="shrink-0 rounded-full border border-[#E2E8F0] px-3 py-1 text-[11px] font-semibold text-[#0A1128] active:scale-95">Übersicht (PDF)</button>
              <button data-testid="csv-export-btn" onClick={() => onCsv(belegJahr)} className="shrink-0 rounded-full border border-[#E2E8F0] px-3 py-1 text-[11px] font-semibold text-[#0A1128] active:scale-95">CSV</button>
              <button data-testid="datev-export-btn" onClick={() => onCsv(belegJahr, "datev")} className="shrink-0 rounded-full border border-[#E2E8F0] px-3 py-1 text-[11px] font-semibold text-[#0A1128] active:scale-95">DATEV</button>
            </div>
          </div>
          <div className="space-y-2.5">
            {auszahlungen.map((a) => (
              <div key={a.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="auszahlung-row">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-base font-bold text-[#0A1128]">{a.kunde_email}</p>
                    <p className="mt-0.5 text-xs text-[#718096]">Angefordert am {fmtDate(a.erstellt_am)}{a.status === "ausgezahlt" && a.ausgezahlt_am ? ` · ausgezahlt am ${fmtDate(a.ausgezahlt_am)}` : ""}</p>
                  </div>
                  <p className="shrink-0 text-xl font-extrabold text-[#8a6d2f]">{a.betrag} €</p>
                </div>
                {(a.iban || a.kontoinhaber) && a.status !== "ausgezahlt" && a.status !== "abgelehnt" && (
                  <div className="mt-2 rounded-xl bg-[#F8F9FA] p-3 text-xs text-[#4A5568]" data-testid="auszahlung-bankdaten">
                    <p><span className="text-[#718096]">Kontoinhaber:</span> {a.kontoinhaber || "—"}</p>
                    <p className="mt-0.5"><span className="text-[#718096]">IBAN:</span> {a.iban || "—"}</p>
                    <p className="mt-0.5"><span className="text-[#718096]">Verwendungszweck:</span> Praemie {a.id?.slice(0, 8)}</p>
                  </div>
                )}
                <div className="mt-3">
                  {a.status === "ausgezahlt" ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-1.5 rounded-xl bg-[#276749]/10 py-2.5 text-sm font-semibold text-[#276749]" data-testid="auszahlung-status-paid">
                        <SealCheck size={15} weight="fill" /> Ausgezahlt
                      </div>
                      <div className="flex gap-2">
                        <button data-testid="auszahlung-beleg-btn" onClick={() => onBeleg(a.id)} className="h-10 flex-1 rounded-xl border border-[#E2E8F0] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">Beleg (PDF)</button>
                        <button data-testid="auszahlung-beleg-mail-btn" onClick={() => onBelegMail(a.id)} className="h-10 flex-1 rounded-xl border border-[#C5A059] bg-[#FBF8F1] text-sm font-semibold text-[#8a6d2f] active:scale-[0.99]">Per E-Mail</button>
                      </div>
                    </div>
                  ) : a.status === "abgelehnt" ? (
                    <div className="rounded-xl bg-[#C0392B]/10 py-2.5 text-center text-sm font-semibold text-[#C0392B]" data-testid="auszahlung-status-rejected">
                      Abgelehnt{a.ablehnung_grund ? <span className="mt-0.5 block text-xs font-normal text-[#C0392B]/80">Grund: {a.ablehnung_grund}</span> : null}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button data-testid="auszahlung-mark-btn" onClick={() => onAusgezahlt(a.id)} className="h-11 flex-1 rounded-xl bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.99]">
                        Als ausgezahlt markieren
                      </button>
                      <button data-testid="auszahlung-reject-btn" onClick={() => onAbgelehnt(a.id)} className="h-11 rounded-xl border border-[#E2E8F0] px-4 text-sm font-semibold text-[#C0392B] active:scale-[0.99]">
                        Ablehnen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {absagen.length > 0 && (
        <div className="mt-8" data-testid="absage-auswertung">
          <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C0392B]">Absage-Gründe ({absagen.length})</h2>
          <div className="space-y-2">
            {absageGruende.map(([grund, count]) => {
              const pct = Math.round((count / absagen.length) * 100);
              return (
                <div key={grund} className="rounded-2xl border border-[#E2E8F0] bg-white p-3" data-testid="absage-grund-row">
                  <div className="flex items-center justify-between gap-3">
                    <span className={`truncate text-sm font-semibold ${grund === "Ohne Angabe" ? "text-[#A0AEC0]" : "text-[#4A5568]"}`}>{grund}</span>
                    <span className="shrink-0 text-sm font-bold text-[#C0392B]">{count}×</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#F1F5F9]">
                    <div className="h-full rounded-full bg-[#C0392B]/60" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {themenStats.length > 0 && (
        <div className="mt-8" data-testid="themen-auswertung">
          <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Gefragteste Themen ({themenTotal})</h2>
          <div className="space-y-2">
            {themenStats.map(([thema, count], idx) => {
              const pct = Math.round((count / themenTotal) * 100);
              const top = idx === 0;
              return (
                <div key={thema} className={`rounded-2xl border p-3 ${top ? "border-[#C5A059] bg-[#FBF8F1] shadow-[0_4px_20px_rgba(197,160,89,0.10)]" : "border-[#E2E8F0] bg-white"}`} data-testid="themen-row">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      {top && <span className="shrink-0 rounded-full bg-[#C5A059] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-testid="themen-top-badge">Top</span>}
                      <span className={`truncate text-sm font-semibold ${top ? "text-[#8a6d2f]" : "text-[#4A5568]"}`}>{thema}</span>
                    </span>
                    <span className="shrink-0 text-sm font-bold text-[#C5A059]">{count}×</span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#F1F5F9]">
                    <div className="h-full rounded-full bg-[#C5A059]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-3 mt-8 flex items-center justify-between">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Letzte Empfehlungen</h2>        {referrals.length > 0 && (
          <button onClick={goList} className="flex items-center gap-1 text-xs font-semibold text-[#0A1128]" data-testid="see-all-btn">
            Alle <ArrowRight size={13} />
          </button>
        )}
      </div>

      {referrals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white px-6 py-12 text-center text-sm text-[#A0AEC0]">
          Noch keine Empfehlungen. Teile deinen QR-Code.
        </div>
      ) : (
        <div className="space-y-2.5">
          {referrals.slice(0, 5).map((r) => (
            <div key={r.id} onClick={goList} className="cursor-pointer rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="uebersicht-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-[#0A1128]">{r.kontakt_name}</p>
                  <p className="mt-0.5 truncate text-xs text-[#718096]">empfohlen von {r.kunde_email}</p>
                </div>
                {(r.praemie_aktiv !== false) && <p className="shrink-0 text-xl font-bold text-[#8a6d2f]">{praemieLabel(r)}</p>}
              </div>
              <div className="mt-2.5">
                <StatusPill status={r.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

const ABSAGE_PRESETS = ["Kein Interesse", "Später", "Zu teuer", "Kein Bedarf", "Nicht erreichbar"];

function AbsageGrund({ referralId, initial }) {
  const [val, setVal] = useState(initial || "");
  const save = (v) => { setVal(v); api.patch(`/referrals/${referralId}/absage-grund`, { grund: v }).catch(() => {}); };
  return (
    <div className="mb-2 w-full" data-testid="absage-grund-block">
      <div className="mb-1.5 flex flex-wrap gap-1.5">
        {ABSAGE_PRESETS.map((p, i) => (
          <button key={p} type="button" data-testid={`absage-preset-${i}`} onClick={() => save(p)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${val === p ? "border-[#C0392B] bg-[#C0392B]/10 text-[#C0392B]" : "border-[#E2E8F0] bg-white text-[#718096]"}`}>{p}</button>
        ))}
      </div>
      <Input data-testid="absage-grund" value={val} placeholder="Eigener Grund (optional)" onChange={(e) => setVal(e.target.value)} onBlur={(e) => save(e.target.value)} className="h-10 rounded-lg border-[#E2E8F0] text-sm" />
    </div>
  );
}

const SACH_PRAEMIEN = [
  "Flasche Wein", "Sektflasche", "Präsentkorb", "Pralinen / Schokolade", "Blumenstrauß",
  "Powerbank", "Bluetooth-Lautsprecher", "Kopfhörer", "Kaffee-Spezialitäten", "Buch nach Wahl",
  "Amazon-Gutschein", "Tankgutschein", "Restaurant-Gutschein", "Kinogutschein",
  "Fitness-/Wellness-Gutschein", "Grillset", "Reise-Gutschein", "Kinderüberraschung / Spielzeug",
];
const praemieLabel = (r) => (r?.praemie_typ === "sache"
  ? ((r.praemie_sache || "Sachprämie") + (r.praemie_betrag ? ` (~${r.praemie_betrag} €)` : ""))
  : `${r?.praemie_betrag || 0} €`);

function InfoHint({ text, testid }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!open) return; const t = setTimeout(() => setOpen(false), 6000); return () => clearTimeout(t); }, [open]);
  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button type="button" data-testid={testid || "info-hint"} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }} className="ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-middle text-[#A0AEC0] hover:text-[#C5A059]">
            <Info size={14} weight="bold" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] text-left leading-snug">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PraemieBetragDialog({ betrag, typ, sache, moneyOnly, onConfirm, trigger }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(String(betrag || 50));
  const [t, setT] = useState(typ || "geld");
  const [s, setS] = useState(sache || "");
  const reset = () => { setVal(String(betrag || 50)); setT(moneyOnly ? "geld" : (typ || "geld")); setS(sache || ""); };
  const submit = () => {
    const n = t === "sache" ? Math.max(0, Math.min(100000, parseInt(val, 10) || 0)) : Math.max(1, Math.min(100000, parseInt(val, 10) || 0));
    onConfirm({ betrag: n, typ: t, sache: t === "sache" ? (s || "").trim() : "" });
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xs rounded-2xl" data-testid="praemie-betrag-dialog">
        <DialogHeader>
          <DialogTitle>Prämie festlegen</DialogTitle>
          <DialogDescription>{t === "sache" ? "Sachprämie wählen (Gegenwert optional)." : "Betrag frei wählbar (in €)."}</DialogDescription>
        </DialogHeader>
        {!moneyOnly && (
          <div className="mb-1 flex gap-2">
            <button type="button" data-testid="dlg-typ-geld" onClick={() => setT("geld")} className={`h-9 flex-1 rounded-full text-xs font-semibold ${t === "geld" ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Geldbetrag</button>
            <button type="button" data-testid="dlg-typ-sache" onClick={() => setT("sache")} className={`h-9 flex-1 rounded-full text-xs font-semibold ${t === "sache" ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Sachprämie</button>
          </div>
        )}
        {t === "sache" && !moneyOnly ? (
          <div className="space-y-2">
            <Select value={SACH_PRAEMIEN.includes(s) ? s : ""} onValueChange={(v) => setS(v)}>
              <SelectTrigger data-testid="dlg-sache-select" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue placeholder="Sachprämie wählen" /></SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="max-h-[46vh]">
                {SACH_PRAEMIEN.map((x) => (<SelectItem key={x} value={x} className="text-sm">{x}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input data-testid="dlg-sache-custom" maxLength={80} value={s} onChange={(e) => setS(e.target.value)} placeholder="oder eigene Sachprämie" className="h-11 rounded-xl border-[#E2E8F0] text-sm" />
            <div className="flex items-center gap-2">
              <Input data-testid="dlg-sache-wert" type="number" inputMode="numeric" min={0} value={val} onChange={(e) => setVal(e.target.value)} className="h-11 rounded-xl border-[#E2E8F0] text-base" />
              <span className="whitespace-nowrap text-xs text-[#718096]">€ Gegenwert (optional)</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input data-testid="praemie-betrag-input" type="number" inputMode="numeric" min={1} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} className="h-11 rounded-xl border-[#E2E8F0] text-base" />
            <span className="text-lg font-semibold text-[#0A1128]">€</span>
          </div>
        )}
        <button type="button" data-testid="praemie-betrag-confirm" onClick={submit} className="h-11 w-full rounded-xl bg-[#C5A059] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">Übernehmen</button>
      </DialogContent>
    </Dialog>
  );
}

function RechnungUpload({ r, onDone }) {
  const [busy, setBusy] = useState(false);
  const [datum, setDatum] = useState(r.rechnung_datum || "");
  const send = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (datum) fd.append("datum", datum);
      await api.post(`/referrals/${r.id}/rechnung`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Beleg gespeichert.");
      onDone && onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-2 rounded-xl border border-[#C5A059]/30 bg-[#FBF8F1] p-2.5" data-testid="rechnung-upload">
      <p className="mb-1.5 text-[11px] font-semibold text-[#8a6d2f]">Sachprämie-Beleg (Rechnung + Datum)</p>
      <div className="flex items-center gap-2">
        <input type="date" data-testid="rechnung-datum" value={datum} onChange={(e) => setDatum(e.target.value)} onBlur={() => datum && datum !== (r.rechnung_datum || "") && send(null)} className="h-9 flex-1 rounded-lg border border-[#E2E8F0] bg-white px-2 text-xs" />
        <label data-testid="rechnung-upload-btn" className="cursor-pointer rounded-full bg-[#0A1128] px-3 py-1.5 text-xs font-semibold text-white active:scale-95">
          {busy ? "…" : (r.rechnung_url ? "Ändern" : "Rechnung")}
          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) send(f); }} />
        </label>
      </div>
      {r.rechnung_url ? <a href={r.rechnung_url} target="_blank" rel="noopener noreferrer" data-testid="rechnung-link" className="mt-1.5 block truncate text-[11px] font-semibold text-[#4A90D9] underline">Rechnung ansehen{r.rechnung_datum ? ` · ${r.rechnung_datum}` : ""}</a> : null}
    </div>
  );
}

function EmpfehlungenList({ referrals, praemieAktiv, defaultBetrag, changeStatus, onReward, onDelete, onBulkDelete, onResend, onAddPraemie, onRemovePraemie, onReload, highlightId }) {
  useEffect(() => {
    if (!highlightId) return;
    const el = document.getElementById(`fall-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId, referrals]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allIds = referrals.map((r) => r.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };
  const doBulk = async () => {
    await onBulkDelete([...selected]);
    exitSelect();
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="tab-empfehlungen">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-3xl font-medium tracking-tight text-[#0A1128]">Empfehlungen</h1>
        {referrals.length > 0 &&
          (selectMode ? (
            <button data-testid="select-cancel-btn" onClick={exitSelect} className="text-sm font-semibold text-[#718096]">Fertig</button>
          ) : (
            <button data-testid="select-mode-btn" onClick={() => setSelectMode(true)} className="text-sm font-semibold text-[#C5A059]">Auswählen</button>
          ))}
      </div>
      {selectMode && (
        <div className="mb-3 flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white px-3 py-2.5" data-testid="bulk-bar">
          <button data-testid="select-all-btn" onClick={toggleAll} className="text-sm font-semibold text-[#0A1128]">{allSelected ? "Keine" : "Alle"}</button>
          <span className="text-xs text-[#718096]" data-testid="selected-count">{selected.size} ausgewählt</span>
          {selected.size > 0 ? (
            <ConfirmDelete name={`${selected.size} Fälle`} testid="bulk-delete-btn" label={`${selected.size} löschen`} onConfirm={doBulk} />
          ) : (
            <span className="text-xs font-semibold text-[#CBD5E0]" data-testid="bulk-delete-btn-disabled">Löschen</span>
          )}
        </div>
      )}
      <div className="space-y-2.5">
        {referrals.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white px-6 py-12 text-center text-sm text-[#A0AEC0]">
            Noch keine Empfehlungen.
          </div>
        )}
        {referrals.map((r) => (
          <div key={r.id} id={`fall-${r.id}`} className={`rounded-2xl border bg-white p-4 ${r.id === highlightId ? "border-[#C5A059] ring-2 ring-[#C5A059]" : selectMode && selected.has(r.id) ? "border-[#C5A059] ring-1 ring-[#C5A059]" : "border-[#E2E8F0]"}`} data-testid="admin-referral-row">
            {selectMode && (
              <label className="mb-2 flex cursor-pointer items-center gap-2">
                <Checkbox data-testid="referral-select-checkbox" checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
                <span className="text-xs font-medium text-[#718096]">Für Löschen auswählen</span>
              </label>
            )}
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 flex-1 truncate text-base font-semibold text-[#0A1128]">{r.kontakt_name}</p>
              {r.praemie_freigeschaltet && ((r.praemie_aktiv !== false) ? (
                <span className="shrink-0 rounded-full bg-[#C5A059]/15 px-2.5 py-0.5 text-xs font-semibold text-[#8a6d2f]">
                  {praemieLabel(r)} {r.praemie_bezahlt ? "bezahlt" : "frei"}
                </span>
              ) : (
                <span className="shrink-0 rounded-full bg-[#276749]/10 px-2.5 py-0.5 text-xs font-semibold text-[#276749]">Erfolgreich</span>
              ))}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {r.bestaetigt ? (
                <span className="flex items-center gap-1 rounded-full bg-[#276749]/10 px-2 py-0.5 text-[11px] font-semibold text-[#276749]">
                  <SealCheck size={12} weight="fill" /> Bestätigt
                </span>
              ) : (
                <span className="flex items-center gap-1 rounded-full bg-[#DD6B20]/10 px-2 py-0.5 text-[11px] font-semibold text-[#DD6B20]">
                  <Clock size={12} /> Wartet auf Bestätigung
                </span>
              )}
            </div>
            {r.bestaetigt ? (
              <div className="mt-1 space-y-0.5">
                {r.kontakt_telefon && <p className="truncate text-xs text-[#718096]"><Phone size={12} className="mr-1 inline" /><a href={`tel:${r.kontakt_telefon}`} className="underline">{r.kontakt_telefon}</a></p>}
                {r.kontakt_email && <p className="truncate text-xs text-[#718096]"><EnvelopeSimple size={12} className="mr-1 inline" /><a href={`mailto:${r.kontakt_email}`} className="underline">{r.kontakt_email}</a></p>}
                {r.anliegen && <p className="mt-1 rounded-md bg-[#FBF8F1] px-2 py-1 text-xs font-medium text-[#8a6d2f]" data-testid="referral-anliegen">💡 {r.anliegen}</p>}
              </div>
            ) : (
              <>
                <p className="mt-1 text-xs italic text-[#A0AEC0]">Kontaktdaten werden sichtbar, sobald die empfohlene Person online zugestimmt hat (DSGVO).<InfoHint testid="hint-dsgvo-freigabe" text="DSGVO: Name und Kontaktdaten der empfohlenen Person werden dir erst angezeigt, nachdem die Person die Weitergabe selbst per E-Mail bestätigt (freigegeben) hat." /></p>
                {r.loesch_am && <p className="mt-1 text-[11px] text-[#DD6B20]">Ohne Bestätigung automatisch gelöscht am {new Date(r.loesch_am).toLocaleDateString("de-DE")}.</p>}
                <button data-testid="resend-confirm-btn" onClick={() => onResend(r.id)} className="mt-2 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-[#C5A059]/40 bg-[#FBF8F1] text-xs font-semibold text-[#8a6d2f] active:scale-[0.99]"><EnvelopeSimple size={14} /> Neue Bestätigungs-Mail senden</button>
              </>
            )}
            <p className="mt-1.5 truncate text-[11px] text-[#A0AEC0]">Geworben von <a href={`mailto:${r.kunde_email}`} data-testid="werber-mailto" className="font-semibold text-[#4A5568] underline">{r.kunde_email}</a>{r.werber_telefon ? <> · <a href={`tel:${r.werber_telefon}`} data-testid="werber-tel" className="font-semibold text-[#4A5568] underline">{r.werber_telefon}</a></> : null}</p>
            {r.bestaetigt && <MailDraftDialog typ="referral" id={r.id} empfaengerName={r.kontakt_name} />}
            <div className="mt-3">
              <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                <SelectTrigger data-testid="status-select" className="h-11 rounded-xl border-[#E2E8F0] bg-[#F8F9FA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`status-option-${o.value}`}>
                      <span className="flex items-center gap-1.5">{o.check && <Check size={15} weight="bold" className="text-[#276749]" />}{o.cross && <X size={15} weight="bold" className="text-[#C0392B]" />}{o.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {r.status === "kunde_geworden" && (
              <div className="mt-2">
                {(r.praemie_aktiv !== false) ? (
                  !r.praemie_freigeschaltet ? (
                    <button data-testid="freigeben-btn" onClick={() => onReward(r.id, "freigeben")} className="h-11 w-full rounded-xl bg-[#C5A059] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">
                      Prämie freigeben ({praemieLabel(r)})
                    </button>
                  ) : !r.praemie_bezahlt ? (
                    <button data-testid="bezahlt-btn" onClick={() => onReward(r.id, "bezahlt")} className="h-11 w-full rounded-xl bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.99]">
                      Als bezahlt markieren
                    </button>
                  ) : (
                    <button data-testid="bezahlt-toggle-btn" onClick={() => onReward(r.id, "bezahlt")} title="Zum Zurücknehmen erneut tippen" className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#276749]/10 py-2.5 text-sm font-semibold text-[#276749] transition-colors hover:bg-[#276749]/15 active:scale-[0.99]">
                      <SealCheck size={15} weight="fill" /> Bezahlt · zum Zurücknehmen tippen
                    </button>
                  )
                ) : !r.praemie_freigeschaltet ? (
                  <button data-testid="freigeben-btn" onClick={() => onReward(r.id, "freigeben")} className="h-11 w-full rounded-xl bg-[#C5A059] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">
                    Als erfolgreich markieren
                  </button>
                ) : (
                  <div className="flex items-center justify-center gap-1.5 rounded-xl bg-[#276749]/10 py-2.5 text-sm font-semibold text-[#276749]" data-testid="erfolgreich-badge">
                    <SealCheck size={15} weight="fill" /> Erfolgreich
                  </div>
                )}
              </div>
            )}
            {r.praemie_typ === "sache" && r.praemie_aktiv !== false && (
              <RechnungUpload r={r} onDone={onReload} />
            )}
            <div className="mt-3 flex flex-col border-t border-[#F1F5F9] pt-2">
              {r.status === "kein_kunde" && (
                <AbsageGrund referralId={r.id} initial={r.absage_grund} />
              )}
              {r.praemie_aktiv === false && (
                <PraemieBetragDialog betrag={defaultBetrag} typ={r.praemie_typ} sache={r.praemie_sache} onConfirm={(p) => onAddPraemie(r.id, p)}
                  trigger={<button type="button" data-testid="add-praemie-btn" className="mb-2 flex h-9 w-full items-center justify-center gap-1.5 rounded-full border border-[#C5A059] bg-[#FBF8F1] text-xs font-semibold text-[#8a6d2f] active:scale-[0.99]">+ Prämie hinzufügen</button>} />
              )}
              {r.praemie_aktiv !== false && !r.praemie_bezahlt && (
                <div className="mb-2 flex gap-2">
                  <PraemieBetragDialog betrag={r.praemie_betrag || defaultBetrag} typ={r.praemie_typ} sache={r.praemie_sache} onConfirm={(p) => onAddPraemie(r.id, p)}
                    trigger={<button type="button" data-testid="edit-praemie-btn" className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full border border-[#C5A059] bg-[#FBF8F1] text-xs font-semibold text-[#8a6d2f] active:scale-[0.99]">Prämie ändern</button>} />
                  <button type="button" data-testid="remove-praemie-btn" onClick={() => onRemovePraemie(r.id)} className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full border border-[#E2E8F0] bg-white text-xs font-semibold text-[#718096] active:scale-[0.99]">Prämie entfernen</button>
                </div>
              )}
              <ConfirmDelete name={r.kontakt_name} testid="referral-delete-btn" label="Fall löschen" onConfirm={() => onDelete(r.id)} className="flex h-9 w-full items-center justify-center gap-1.5 rounded-full border border-[#C0392B]/40 bg-white text-xs font-semibold text-[#C0392B] transition-colors hover:bg-[#C0392B]/5 active:scale-[0.99]" />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function QrPanel({ user, beraterCode, name, praemie, praemieAktiv = true, slogan = "", refresh }) {
  const [image, setImage] = useState("");
  const [full, setFull] = useState(false);
  const [posterBusy, setPosterBusy] = useState(false);
  const [posterPreview, setPosterPreview] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [variant, setVariant] = useState("standard");
  const [qrMode, setQrMode] = useState(praemieAktiv ? "an" : "aus");
  const [rewardTyp, setRewardTyp] = useState(user?.praemie_typ || "geld");
  const [rewardSache, setRewardSache] = useState(user?.praemie_sache || "");
  const sachOpts = user?.sach_optionen?.length ? user.sach_optionen : SACH_PRAEMIEN;
  useEffect(() => { setRewardTyp(user?.praemie_typ || "geld"); setRewardSache(user?.praemie_sache || ""); }, [user?.praemie_typ, user?.praemie_sache]);
  const [sloganVariante, setSloganVariante] = useState(user?.slogan_variante || (praemieAktiv ? "praemie" : "vertrauen"));
  const [sloganCustom, setSloganCustom] = useState(user?.slogan_custom || "");
  useEffect(() => { setSloganVariante(user?.slogan_variante || (praemieAktiv ? "praemie" : "vertrauen")); setSloganCustom(user?.slogan_custom || ""); }, [user?.slogan_variante, user?.slogan_custom, praemieAktiv]);
  const effSlogan = sloganCustom.trim() || sloganTextFor(sloganVariante, user?.branche || "finanz");
  const saveSlogan = async (patch) => { try { await api.patch("/berater/profile", patch); if (refresh) await refresh(); toast.success("Slogan aktualisiert."); } catch { toast.error("Konnte nicht speichern."); } };
  const link = (() => {
    const base = `${window.location.origin}/s/${beraterCode || ""}`;
    return (qrMode === "aus" && praemieAktiv !== false) ? `${base}?p=0` : base;
  })();
  const variants = qrMode === "an" ? POSTER_VARIANTS_PRAEMIE : POSTER_VARIANTS_NEUTRAL;

  useEffect(() => {
    const keys = (qrMode === "an" ? POSTER_VARIANTS_PRAEMIE : POSTER_VARIANTS_NEUTRAL).map((v) => v.key);
    if (!keys.includes(variant)) setVariant(keys[0]);
  }, [qrMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!beraterCode) return;
    api.get("/qr", { params: { data: link } }).then(({ data }) => setImage(data.image)).catch(() => {});
  }, [beraterCode, link]);

  function buildPosterParams() {
    return { data: link, name: name || "", variant, betrag: qrMode === "an" ? (praemie || 0) : 0, praemie_text: (qrMode === "an" && rewardTyp === "sache") ? (rewardSache || "") : "", brand: qrMode === "an" ? "Prämio" : "Empfehlungs-Kompass", subtitle: effSlogan || "", logo_url: user?.logo_url || "", portrait_url: user?.portrait_url || "", personal_logo_url: user?.personal_logo_url || "", logo_pos: user?.branding_pos?.poster?.firmenlogo || "aus", portrait_pos: user?.branding_pos?.poster?.portrait || "aus", personal_pos: user?.branding_pos?.poster?.persoenlich || "aus", telefon: user?.telefon_anzeigen ? (user?.telefon || "") : "", email: user?.email_anzeigen ? (user?.email || "") : "", whatsapp: user?.whatsapp_anzeigen ? (user?.whatsapp || "") : "", kopfzeile: user?.firma_name || user?.zusatz || "" };
  }

  useEffect(() => {
    if (!beraterCode) return;
    let cancel = false;
    setPreviewBusy(true);
    const t = setTimeout(() => {
      api.get("/qr/poster", { params: buildPosterParams() })
        .then(({ data }) => { if (!cancel) setPosterPreview(data.image); })
        .catch(() => {})
        .finally(() => { if (!cancel) setPreviewBusy(false); });
    }, 450);
    return () => { cancel = true; clearTimeout(t); };
  }, [beraterCode, variant, qrMode, rewardTyp, rewardSache, effSlogan, name, praemie, user?.logo_url, user?.portrait_url, user?.personal_logo_url, user?.branding_pos, user?.telefon_anzeigen, user?.telefon, user?.email_anzeigen, user?.email, user?.whatsapp_anzeigen, user?.whatsapp, user?.firma_name, user?.zusatz]);  // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async () => {
    const ok = await copyText(link);
    ok ? toast.success("Link kopiert.") : toast.error("Konnte nicht kopieren.");
  };
  const share = async () => {
    const res = await shareLink({ title: "Empfehlung", text: "Jetzt scannen & empfehlen", url: link });
    if (res === "copied") toast.success("Link kopiert.");
    else if (res === "failed") toast.error("Teilen nicht möglich.");
  };
  const showLinkToast = (headline) => {
    toast(
      <div className="flex flex-col gap-1" data-testid="poster-link-toast">
        <span className="text-sm font-semibold text-[#0A1128]">{headline}</span>
        <span className="text-[11px] text-[#718096]">Diesen Link kannst du direkt weitergeben – man muss den QR-Code nicht scannen.</span>
        <a href={link} target="_blank" rel="noopener noreferrer" data-testid="poster-link-anchor" className="mt-0.5 break-all text-xs font-semibold text-[#4A90D9] underline">{link}</a>
      </div>,
      { duration: 15000, action: { label: "Kopieren", onClick: () => { copyText(link).then((ok) => { if (ok) toast.success("Link kopiert."); }); } } }
    );
  };
  const sharePosterLinkOnly = async () => {
    const res = await shareLink({ title: "Empfehlung", text: "Jetzt empfehlen – hier geht's direkt:", url: link });
    if (res === "failed") { toast.error("Teilen nicht möglich."); return; }
    if (res !== "shared") showLinkToast("Dein Empfehlungs-Link:");
  };
  const sharePoster = async () => {
    setPosterBusy(true);
    try {
      const { data } = await api.get("/qr/poster", { params: buildPosterParams() });
      const res = await shareImage(data.image, "praemio-empfehlung.png", { title: "Empfehlung", text: `Jetzt empfehlen – hier geht's direkt: ${link}` });
      if (res === "failed") { toast.error("Poster konnte nicht erstellt werden."); return; }
      showLinkToast(res === "downloaded" ? "Poster gespeichert – Link zum Teilen:" : "Poster geteilt – Link zum Weitergeben:");
    } catch {
      toast.error("Poster konnte nicht erstellt werden.");
    } finally {
      setPosterBusy(false);
    }
  };

  const [hl1, hl2] = POSTER_HEADLINES[variant] || POSTER_HEADLINES.standard;
  const ring = qrMode === "aus" ? "#4A90D9" : "#C5A059";

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center pt-2" data-testid="tab-qr">
      <h1 className="text-center text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0A1128]" data-testid="qr-headline">
        {hl1}<br />{hl2}
      </h1>

      <div className="relative mt-7 w-full overflow-hidden rounded-[28px] border border-[#E2E8F0] bg-white p-5 shadow-[0_18px_50px_rgba(10,17,40,0.10)] sm:p-6">
        <div className="pointer-events-none absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(197,160,89,0.16),transparent_62%)]" />
        <button
          data-testid="qr-scan-button"
          onClick={() => setFull(true)}
          className="relative mx-auto block rounded-3xl border-[3px] bg-white p-3 shadow-[0_8px_24px_rgba(74,144,217,0.10)] transition-transform active:scale-95 sm:p-4"
          style={{ borderColor: ring }}
          aria-label="QR-Code groß anzeigen"
        >
          {image ? (
            <img src={image} alt="QR-Code" className="h-[min(64vw,256px)] w-[min(64vw,256px)]" data-testid="qr-image" />
          ) : (
            <div className="flex h-[min(64vw,256px)] w-[min(64vw,256px)] items-center justify-center text-sm text-[#A0AEC0]">Lädt…</div>
          )}
        </button>
        <p className="relative mt-5 text-center text-sm font-medium text-[#718096]">In 1 Sekunde scannen</p>
      </div>

      <button
        data-testid="qr-fullscreen-btn"
        onClick={() => setFull(true)}
        className="mt-6 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-base font-semibold text-white transition-colors hover:bg-[#141d3a] active:scale-[0.99]"
      >
        <Sun size={20} weight="fill" color="#C5A059" /> Vollbild
      </button>
      <div className="mt-2.5 grid w-full grid-cols-2 gap-2.5">
        <button data-testid="qr-share-btn" onClick={share} className="flex h-11 items-center justify-center gap-1.5 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-[#F1F5F9]">
          <ShareNetwork size={16} /> Link senden
        </button>
        <button data-testid="copy-link-btn" onClick={copy} className="flex h-11 items-center justify-center gap-1.5 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-[#F1F5F9]">
          <Copy size={16} /> Kopieren
        </button>
      </div>
      <div className="mt-5 w-full">
        <p className="mb-2 text-center text-xs font-semibold text-[#718096]">Was zeigt dieser QR-Code?</p>
        <div className="flex gap-2" data-testid="qr-mode-toggle">
          <button type="button" data-testid="qr-mode-an" onClick={() => setQrMode("an")} className={`h-11 flex-1 rounded-full text-sm font-semibold transition-all ${qrMode === "an" ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Mit Prämie</button>
          <button type="button" data-testid="qr-mode-aus" onClick={() => setQrMode("aus")} className={`h-11 flex-1 rounded-full text-sm font-semibold transition-all ${qrMode === "aus" ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Ohne Prämie</button>
        </div>
        <p className="mt-1.5 text-center text-[11px] text-[#A0AEC0]">Zeig je nach Person den passenden Code – „Mit Prämie" oder „Ohne Prämie".</p>
        {qrMode === "an" && (
          <div className="mt-3 rounded-2xl border border-[#E2E8F0] bg-white p-3" data-testid="qr-reward-typ">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Belohnung für diese Variante<InfoHint testid="hint-qr-reward" text="Standard ist Geld. Hier wählst du je Aushang/Person, ob Geld- oder Sachprämie – erscheint auf Poster, Link und Startseite." /></p>
            <div className="flex gap-2">
              <button type="button" data-testid="qr-reward-geld" onClick={() => setRewardTyp("geld")} className={`h-9 flex-1 rounded-full text-xs font-semibold ${rewardTyp === "geld" ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Geldprämie</button>
              <button type="button" data-testid="qr-reward-sache" onClick={() => setRewardTyp("sache")} className={`h-9 flex-1 rounded-full text-xs font-semibold ${rewardTyp === "sache" ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Sachprämie</button>
            </div>
            {rewardTyp === "sache" && (
              <Select value={sachOpts.includes(rewardSache) ? rewardSache : ""} onValueChange={(v) => setRewardSache(v)}>
                <SelectTrigger data-testid="qr-reward-sache-select" className="mt-2 h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue placeholder="Sachprämie wählen" /></SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="max-h-[46vh]">
                  {sachOpts.map((s) => (<SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <p className="mt-1.5 text-[11px] leading-snug text-[#A0AEC0]">Standard ist Geld – hier wählst du je Aushang/Person, was auf Poster, Link und Startseite als Dankeschön erscheint. Eigene (freie) Sachprämien erscheinen auf dem Poster.</p>
          </div>
        )}
      </div>

      <div className="mt-4 w-full rounded-2xl border border-[#E2E8F0] bg-white p-3" data-testid="qr-slogan-edit">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Slogan (Poster & Startseite)</p>
        <Select value={sloganVariante} onValueChange={(v) => { setSloganVariante(v); setSloganCustom(""); saveSlogan({ slogan_variante: v, slogan_custom: "" }); }}>
          <SelectTrigger data-testid="qr-slogan-dropdown" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue /></SelectTrigger>
          <SelectContent position="popper" sideOffset={4} className="max-h-[50vh] max-w-[calc(100vw-3rem)] overflow-y-auto">
            {SLOGAN_OPTIONS.map((o) => (<SelectItem key={o.k} value={o.k} data-testid={`qr-slogan-option-${o.k}`} className="text-sm">{o.t}</SelectItem>))}
            {themenFor(user?.branche || "finanz").map((c, i) => (<SelectItem key={`qs-${i}`} value={`thema_${user?.branche || "finanz"}_${i}`} data-testid={`qr-slogan-thema-${i}`} className="text-sm">{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input data-testid="qr-slogan-custom" maxLength={120} value={sloganCustom} onChange={(e) => setSloganCustom(e.target.value)} onBlur={() => saveSlogan({ slogan_custom: sloganCustom })} placeholder="Eigener Slogan (überschreibt Vorlage)" className="mt-2 h-11 w-full rounded-lg border-[#E2E8F0] bg-white text-sm focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
        <p className="mt-1 text-[11px] leading-snug text-[#A0AEC0]">Gilt fürs Poster und deine Startseite – wird sofort gespeichert. Mehr Feineinstellungen unter „Profil".</p>
      </div>

      <div className={`mt-4 grid w-full gap-2 ${variants.length === 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid="poster-variants">
        {variants.map((v) => (
          <button
            key={v.key}
            data-testid={`poster-variant-${v.key}`}
            onClick={() => setVariant(v.key)}
            className={`h-9 rounded-full text-xs font-semibold transition-colors ${variant === v.key ? "bg-[#0A1128] text-white" : "border border-[#0A1128]/15 bg-white text-[#718096]"}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div className="mt-4 w-full" data-testid="poster-preview-wrap">
        <p className="mb-2 text-center text-xs font-semibold text-[#718096]">Vorschau</p>
        <div className="relative mx-auto w-full max-w-[300px] overflow-hidden rounded-2xl border border-[#E2E8F0] bg-[#0A1128] shadow-[0_10px_30px_rgba(10,17,40,0.18)]" style={{ aspectRatio: "1080 / 1850" }}>
          {posterPreview ? (
            <img src={posterPreview} alt="Poster-Vorschau" data-testid="poster-preview-image" className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-white/50">Lädt…</div>
          )}
          {previewBusy && posterPreview ? (
            <div className="absolute right-2 top-2 rounded-full bg-black/45 px-2 py-1 text-[10px] font-medium text-white/85" data-testid="poster-preview-updating">aktualisiert…</div>
          ) : null}
        </div>
      </div>
      <button
        data-testid="qr-poster-btn"
        onClick={sharePoster}
        disabled={posterBusy}
        className="mt-2.5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#C5A059] text-sm font-semibold text-[#0A1128] shadow-[0_8px_24px_rgba(197,160,89,0.3)] transition-all active:scale-[0.99] disabled:opacity-60"
      >
        <Export size={18} weight="bold" /> {posterBusy ? "Erstelle Poster…" : "Poster teilen"}
      </button>
      <button
        data-testid="qr-poster-link-only-btn"
        onClick={sharePosterLinkOnly}
        className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[#0A1128]/15 bg-white text-sm font-semibold text-[#0A1128] transition-colors hover:bg-[#F1F5F9] active:scale-[0.99]"
      >
        <LinkSimple size={17} weight="bold" /> Nur Link teilen
      </button>
      <p className="mt-1.5 text-center text-[11px] leading-snug text-[#A0AEC0]">Ideal, wenn dein Gegenüber den QR-Code nicht scannen kann – dann einfach nur den Link senden.</p>

      <AnimatePresence>
        {full && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setFull(false)}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white px-6"
            data-testid="qr-fullscreen"
          >
            <button
              data-testid="qr-fullscreen-close"
              onClick={() => setFull(false)}
              style={{ top: "calc(env(safe-area-inset-top) + 3.5rem)" }}
              className="absolute right-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#F1F5F9] text-[#0A1128]"
              aria-label="Schließen"
            >
              <X size={22} />
            </button>
            <p className="mb-6 text-[12px] font-bold uppercase tracking-[0.14em] text-[#C5A059]">{hl1}</p>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="rounded-3xl border-4 border-[#0A1128] bg-white p-4">
              {image ? (
                <img src={image} alt="QR-Code" className="h-[min(80vw,440px)] w-[min(80vw,440px)]" data-testid="qr-fullscreen-image" />
              ) : (
                <div className="flex h-72 w-72 items-center justify-center text-sm text-[#A0AEC0]">Lädt…</div>
              )}
            </motion.div>
            {name && <p className="mt-6 text-2xl font-bold text-[#0A1128]">{name}</p>}
            <p className="mt-1 text-sm text-[#718096]">Kamera öffnen und Code scannen</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function formatPhone(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  const hasIntl = s.startsWith("+") || s.startsWith("00");
  const norm = s.startsWith("00") ? "+" + s.slice(2) : s;
  const parsed = parsePhoneNumberFromString(norm, hasIntl ? undefined : "DE");
  if (parsed && parsed.isValid()) return parsed.formatInternational();
  return new AsYouType(hasIntl ? undefined : "DE").input(norm) || s;
}

const KONTAKT_LABELS = { telefon: "📞 Telefon (Anruf)", whatsapp: "💬 WhatsApp", email: "📧 E-Mail", webseite: "🌐 Webseite" };

function LandingPreview({ beraterCode, praemieAktiv, reloadKey }) {
  const [previewMode, setPreviewMode] = useState(praemieAktiv ? "an" : "aus");
  const [full, setFull] = useState(false);
  useEffect(() => { setPreviewMode(praemieAktiv ? "an" : "aus"); }, [praemieAktiv]);
  const bust = encodeURIComponent(`${reloadKey || ""}|${previewMode}`);
  const src = `${window.location.origin}/start/${beraterCode || ""}?pmode=${previewMode}&preview=1&v=${bust}`;
  const modeToggle = (dark) => (
    <div className={`inline-flex rounded-full border p-0.5 ${dark ? "border-white/15 bg-white/5" : "border-[#E2E8F0] bg-[#F8F9FA]"}`} data-testid={dark ? "preview-full-mode-toggle" : "preview-mode-toggle"}>
      <button type="button" data-testid={dark ? "preview-full-mode-an" : "preview-mode-an"} onClick={() => setPreviewMode("an")} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${previewMode === "an" ? "bg-[#C5A059] text-[#0A1128]" : (dark ? "text-white/70" : "text-[#718096]")}`}>Mit Prämie</button>
      <button type="button" data-testid={dark ? "preview-full-mode-aus" : "preview-mode-aus"} onClick={() => setPreviewMode("aus")} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${previewMode === "aus" ? (dark ? "bg-white text-[#0A1128]" : "bg-[#0A1128] text-white") : (dark ? "text-white/70" : "text-[#718096]")}`}>Ohne Prämie</button>
    </div>
  );
  return (
    <div className="mt-2 flex flex-col items-center" data-testid="slogan-preview">
      <div className="mb-2">{modeToggle(false)}</div>
      <button type="button" onClick={() => setFull(true)} data-testid="preview-open-full" aria-label="Vorschau im Vollbild öffnen" className="relative block overflow-hidden rounded-[2rem] border-4 border-[#1a2338] bg-[#0A1128] shadow-xl transition-transform active:scale-[0.98]" style={{ width: 232, height: 470 }}>
        <div className="pointer-events-none absolute left-1/2 top-0 z-10 h-4 w-20 -translate-x-1/2 rounded-b-xl bg-[#1a2338]" />
        <iframe
          key={`${reloadKey}|${previewMode}`}
          data-testid="landing-preview-frame"
          title="Vorschau deiner Startseite"
          src={src}
          scrolling="no"
          className="origin-top-left border-0"
          style={{ width: 420, height: 851, transform: "scale(0.552)", pointerEvents: "none" }}
        />
        <span className="pointer-events-none absolute bottom-2 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/60 px-3 py-1 text-[10px] font-semibold text-white backdrop-blur-sm">Tippen zum Vergrößern</span>
      </button>
      <p className="mt-2 text-center text-[11px] text-[#A0AEC0]">So sieht deine komplette Startseite für Kunden aus.</p>

      <AnimatePresence>
        {full && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex flex-col bg-[#0A1128]" data-testid="preview-fullscreen">
            <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]">
              {modeToggle(true)}
              <button type="button" onClick={() => setFull(false)} data-testid="preview-full-close" aria-label="Vollbild schließen" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-lg leading-none text-white active:scale-95">✕</button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                key={`full|${reloadKey}|${previewMode}`}
                data-testid="preview-fullscreen-frame"
                title="Vollbild-Vorschau deiner Startseite"
                src={src}
                className="h-full w-full border-0 bg-[#0A1128]"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Profil({ user, refresh, logout }) {
  const [form, setForm] = useState({ name: "", firma: "", telefon: "" });
  const [firmaAnzeigen, setFirmaAnzeigen] = useState(false);
  const [telefonAnzeigen, setTelefonAnzeigen] = useState(false);
  const [emailAnzeigen, setEmailAnzeigen] = useState(false);
  const [whatsappAnzeigen, setWhatsappAnzeigen] = useState(false);
  const [webseiteAnzeigen, setWebseiteAnzeigen] = useState(false);
  const [kontaktOrder, setKontaktOrder] = useState(user?.kontakt_reihenfolge?.length ? user.kontakt_reihenfolge : ["telefon", "whatsapp", "email", "webseite"]);
  const [notifClean, setNotifClean] = useState(!!user?.notif_autoclean);
  const [autodelUn, setAutodelUn] = useState(!!user?.autodelete_unbestaetigt);
  const [autodelTage, setAutodelTage] = useState(user?.autodelete_tage || 14);
  const [zusatzAnzeigen, setZusatzAnzeigen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [praemie, setPraemie] = useState(user?.praemie_betrag ?? 50);
  const [pBusy, setPBusy] = useState(false);
  const [praemieTyp, setPraemieTyp] = useState(user?.praemie_typ || "geld");
  const [praemieSache, setPraemieSache] = useState(user?.praemie_sache || "");
  const [sachOptionen, setSachOptionen] = useState(user?.sach_optionen?.length ? user.sach_optionen : SACH_PRAEMIEN);
  const [sachNew, setSachNew] = useState("");
  const [praemieAktiv, setPraemieAktiv] = useState(user?.praemie_aktiv !== false);
  const [sloganVariante, setSloganVariante] = useState(user?.slogan_variante || (user?.praemie_aktiv !== false ? "praemie" : "vertrauen"));
  const [claim, setClaim] = useState(user?.claim || (user?.praemie_aktiv !== false ? "Empfehlen & Profitieren" : "Weiterempfehlen aus Überzeugung"));
  const [claimCustom, setClaimCustom] = useState(user?.claim_custom || "");
  const [sloganCustom, setSloganCustom] = useState(user?.slogan_custom || "");
  const [subtext, setSubtext] = useState(user?.subtext || (user?.praemie_aktiv !== false ? SUBTEXT_OPTIONS_PRAEMIE[0] : SUBTEXT_OPTIONS_NEUTRAL[0]));
  const [subtextCustom, setSubtextCustom] = useState(user?.subtext_custom || "");
  const [themenAn, setThemenAn] = useState(user?.themen_anzeigen ?? ((user?.branche || "finanz") !== "finanz"));
  const [themenCustom, setThemenCustom] = useState(user?.themen_custom || []);
  const saveThemenCustom = (arr) => { const clean = arr.map((t) => (t || "").trim().slice(0, 80)).filter(Boolean).slice(0, 5); saveField({ themen_custom: clean }); };
  useEffect(() => {
    setForm({ name: user?.name || "", anzeige_name: user?.anzeige_name || "", firma: user?.firma || "", telefon: formatPhone(user?.telefon || ""), whatsapp: formatPhone(user?.whatsapp || ""), zusatz: user?.zusatz || "", datenschutz_url: user?.datenschutz_url || "", webseite: user?.webseite || "", karriere_text: user?.karriere_text || "", karriere_variante: user?.karriere_variante || "" });
    setFirmaAnzeigen(!!user?.firma_anzeigen);
    setTelefonAnzeigen(!!user?.telefon_anzeigen);
    setEmailAnzeigen(!!user?.email_anzeigen);
    setWhatsappAnzeigen(!!user?.whatsapp_anzeigen);
    setWebseiteAnzeigen(!!user?.webseite_anzeigen);
    setKontaktOrder(user?.kontakt_reihenfolge?.length ? user.kontakt_reihenfolge : ["telefon", "whatsapp", "email", "webseite"]);
    setNotifClean(!!user?.notif_autoclean);
    setAutodelUn(!!user?.autodelete_unbestaetigt);
    setAutodelTage(user?.autodelete_tage || 14);
    setZusatzAnzeigen(!!user?.zusatz_anzeigen);
    setThemenAn(user?.themen_anzeigen ?? ((user?.branche || "finanz") !== "finanz"));
    setThemenCustom(user?.themen_custom || []);
  }, [user?.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPraemie(user?.praemie_betrag ?? 50); setPraemieTyp(user?.praemie_typ || "geld"); setPraemieSache(user?.praemie_sache || ""); setSachOptionen(user?.sach_optionen?.length ? user.sach_optionen : SACH_PRAEMIEN); }, [user?.praemie_betrag, user?.praemie_typ, user?.praemie_sache, user?.sach_optionen]);
  useEffect(() => { const pa = user?.praemie_aktiv !== false; setPraemieAktiv(pa); setSloganVariante(user?.slogan_variante || (pa ? "praemie" : "vertrauen")); setClaim(user?.claim || (pa ? "Empfehlen & Profitieren" : "Weiterempfehlen aus Überzeugung")); setClaimCustom(user?.claim_custom || ""); setSloganCustom(user?.slogan_custom || ""); setSubtext(user?.subtext || (pa ? SUBTEXT_OPTIONS_PRAEMIE[0] : SUBTEXT_OPTIONS_NEUTRAL[0])); setSubtextCustom(user?.subtext_custom || ""); }, [user?.praemie_aktiv, user?.slogan_variante, user?.claim, user?.claim_custom, user?.slogan_custom, user?.subtext, user?.subtext_custom]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch("/berater/profile", { ...form, firma_anzeigen: firmaAnzeigen });
      await refresh();
      toast.success("Profil gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  const resetAllPraemien = async () => {
    if (!window.confirm("Alle noch nicht freigeschalteten Prämien auf deinen aktuellen Standard zurücksetzen? Bereits freigeschaltete/ausgezahlte Prämien bleiben unverändert.")) return;
    try {
      const { data } = await api.post("/berater/praemien/reset");
      await refresh();
      toast.success(`${data.updated} Empfehlung(en) zurückgesetzt.`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  const resetDefaults = async () => {
    if (!window.confirm("Alle Anzeige-, Text- und Branding-Einstellungen auf die Werkseinstellung zurücksetzen? Deine Empfehlungen, Auszahlungen und ausgezahlten Prämien bleiben unverändert erhalten.")) return;
    try { await api.post("/berater/reset-defaults"); await refresh(); toast.success("Auf Werkseinstellung zurückgesetzt."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  const deleteAccount = async () => {
    const t = window.prompt("Dein Konto wird dauerhaft gelöscht und du kannst dich danach nicht mehr anmelden. Deine Profildaten werden entfernt. Bereits ausgezahlte Prämien und Steuerbelege bleiben aus gesetzlichen Gründen anonymisiert gespeichert.\n\nTippe zum Bestätigen LÖSCHEN ein:");
    if ((t || "").trim().toUpperCase() !== "LÖSCHEN") return;
    try { await api.post("/berater/account-delete"); toast.success("Dein Konto wurde gelöscht."); logout(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  const saveSachOptionen = (arr) => { const c = [...new Set(arr.map((s) => (s || "").trim().slice(0, 80)).filter(Boolean))].slice(0, 40); setSachOptionen(c.length ? c : SACH_PRAEMIEN); saveField({ sach_optionen: c }); };
  const addSach = () => { const v = sachNew.trim(); if (!v) return; saveSachOptionen([...sachOptionen, v]); setSachNew(""); };
  const removeSach = (s) => saveSachOptionen(sachOptionen.filter((x) => x !== s));
  const savePraemie = async () => {
    setPBusy(true);
    try {
      await api.patch("/berater/praemie", { betrag: Number(praemie) || 0, typ: praemieTyp, sache: praemieSache });
      await refresh();
      toast.success("Prämie gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setPBusy(false);
    }
  };
  const savePraemieMode = async (aktiv, variante) => {
    try {
      await api.patch("/berater/profile", { praemie_aktiv: aktiv, slogan_variante: variante ?? sloganVariante });
      await refresh();
      toast.success(aktiv ? "Geldprämie aktiviert." : "Ohne Geldprämie – Wortlaut gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const saveClaim = async () => {
    try {
      await api.patch("/berater/profile", { claim });
      await refresh();
      toast.success("Claim gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const saveClaimValue = async (v) => {
    try {
      await api.patch("/berater/profile", { claim: v });
      await refresh();
      toast.success("Claim gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const saveSloganCustom = async () => {
    try {
      await api.patch("/berater/profile", { slogan_custom: sloganCustom });
      await refresh();
      toast.success("Eigener Slogan gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const saveField = async (patch) => {
    try {
      await api.patch("/berater/profile", patch);
      await refresh();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const moveKontakt = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= kontaktOrder.length) return;
    const arr = [...kontaktOrder];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setKontaktOrder(arr);
    saveField({ kontakt_reihenfolge: arr });
  };
  const [logoUrl, setLogoUrl] = useState(user?.logo_url || "");
  const [portraitUrl, setPortraitUrl] = useState(user?.portrait_url || "");
  const [personalLogoUrl, setPersonalLogoUrl] = useState(user?.personal_logo_url || "");
  const DEFAULT_POS = { startseite: { firmenlogo: "aus", persoenlich: "aus", portrait: "aus" }, poster: { firmenlogo: "aus", persoenlich: "aus", portrait: "aus" }, pdf: { firmenlogo: "aus", persoenlich: "aus", portrait: "aus" } };
  const [brandingPos, setBrandingPos] = useState(user?.branding_pos || DEFAULT_POS);
  const [cropKind, setCropKind] = useState(null); // "logo" | "portrait" | "personal"
  const [cropSrc, setCropSrc] = useState(null);
  useEffect(() => {
    setLogoUrl(user?.logo_url || "");
    setPortraitUrl(user?.portrait_url || "");
    setPersonalLogoUrl(user?.personal_logo_url || "");
    if (user?.branding_pos) setBrandingPos(user.branding_pos);
  }, [user?.logo_url, user?.portrait_url, user?.personal_logo_url, user?.branding_pos]);

  const EL_OF_KIND = { logo: "firmenlogo", portrait: "portrait", personal: "persoenlich" };
  const setSlot = (surface, el, val) => {
    setBrandingPos((prev) => {
      const surf = { ...(prev[surface] || {}) };
      if (val !== "aus") {
        for (const e of ["firmenlogo", "persoenlich", "portrait"]) { if (e !== el && surf[e] === val) surf[e] = "aus"; }
      }
      surf[el] = val;
      const next = { ...prev, [surface]: surf };
      saveField({ branding_pos: next });
      return next;
    });
  };
  const autoPlace = (el) => {
    setBrandingPos((prev) => {
      if ((prev.startseite?.[el] || "aus") !== "aus") return prev;
      const used = new Set(Object.entries(prev.startseite || {}).filter(([k]) => k !== el).map(([, v]) => v));
      const free = ["rechts", "links", "karte"].find((s) => !used.has(s));
      if (!free) return prev;
      const next = { ...prev, startseite: { ...prev.startseite, [el]: free } };
      saveField({ branding_pos: next });
      return next;
    });
  };

  const onPickFile = (kind) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Bitte ein Bild auswählen."); return; }
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result); setCropKind(kind); };
    reader.readAsDataURL(file);
  };
  const uploadCropped = async (blob) => {
    const kind = cropKind;
    const fd = new FormData();
    fd.append("file", blob, `${kind}.png`);
    try {
      const { data } = await api.post(`/berater/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "logo") setLogoUrl(data.url);
      else if (kind === "portrait") setPortraitUrl(data.url);
      else setPersonalLogoUrl(data.url);
      autoPlace(EL_OF_KIND[kind]);
      await refresh();
      toast.success(kind === "portrait" ? "Foto gespeichert." : "Logo gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const removeImage = async (kind) => {
    const el = EL_OF_KIND[kind];
    const field = { logo: "logo_url", portrait: "portrait_url", personal: "personal_logo_url" }[kind];
    if (kind === "logo") setLogoUrl(""); else if (kind === "portrait") setPortraitUrl(""); else setPersonalLogoUrl("");
    const next = { ...brandingPos };
    for (const s of ["startseite", "poster", "pdf"]) next[s] = { ...(next[s] || {}), [el]: "aus" };
    setBrandingPos(next);
    await saveField({ [field]: "", branding_pos: next });
  };
  const saveSubtext = async () => {
    try {
      await api.patch("/berater/profile", { subtext });
      await refresh();
      toast.success("Text gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };
  const saveSubtextValue = async (v) => {
    try {
      await api.patch("/berater/profile", { subtext: v });
      await refresh();
      toast.success("Text gespeichert.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const F = (k, label, type = "text") => (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">{label}</label>
      <Input data-testid={`profile-${k}`} type={type} inputMode={type === "tel" ? "tel" : undefined} autoComplete={type === "tel" ? "tel" : undefined} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} onBlur={() => saveField({ [k]: form[k] })} className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
    </div>
  );

  const subtextOpts = praemieAktiv ? SUBTEXT_OPTIONS_PRAEMIE : SUBTEXT_OPTIONS_NEUTRAL;
  const contentFields = (aktiv) => (
    <>
      <div>
        <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Claim unter dem Logo (optional)</label>
        <Select value={claim || "__none__"} onValueChange={(v) => { const val = v === "__none__" ? "" : v; setClaim(val); saveField({ claim: val }); }}>
          <SelectTrigger data-testid="claim-dropdown" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue placeholder="Vorlage wählen …" /></SelectTrigger>
          <SelectContent position="popper" sideOffset={4} className="max-h-[50vh] max-w-[calc(100vw-3rem)] overflow-y-auto">
            <SelectItem value="__none__" data-testid="claim-option-none" className="text-sm">— ohne Claim —</SelectItem>
            {CLAIM_OPTIONS.map((c) => (<SelectItem key={c} value={c} data-testid={`claim-option-${c}`} className="text-sm">{c}</SelectItem>))}
            {themenFor(user?.branche || "finanz").map((c) => (<SelectItem key={`tc-${c}`} value={c} data-testid={`claim-thema-${c}`} className="text-sm">{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input data-testid="claim-input" maxLength={80} value={claimCustom} onChange={(e) => setClaimCustom(e.target.value)} onBlur={() => saveField({ claim_custom: claimCustom })} placeholder="Eigener Claim (überschreibt Vorlage)" className="mt-2 h-11 w-full rounded-lg border-[#E2E8F0] bg-white text-sm focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
        <p className="mt-1 text-right text-[11px] text-[#A0AEC0]" data-testid="claim-custom-count">{claimCustom.length}/80</p>
      </div>
      <div>
        <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Slogan (große Überschrift)</label>
        <Select value={sloganVariante} onValueChange={(v) => { setSloganVariante(v); savePraemieMode(aktiv, v); }}>
          <SelectTrigger data-testid="slogan-dropdown" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue /></SelectTrigger>
          <SelectContent position="popper" sideOffset={4} className="max-h-[50vh] max-w-[calc(100vw-3rem)] overflow-y-auto">
            {SLOGAN_OPTIONS.map((o) => (<SelectItem key={o.k} value={o.k} data-testid={`slogan-option-${o.k}`} className="text-sm">{o.t}</SelectItem>))}
            {themenFor(user?.branche || "finanz").map((c, i) => (<SelectItem key={`ts-${i}`} value={`thema_${user?.branche || "finanz"}_${i}`} data-testid={`slogan-thema-${i}`} className="text-sm">{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input data-testid="slogan-custom-input" maxLength={120} value={sloganCustom} onChange={(e) => setSloganCustom(e.target.value)} onBlur={saveSloganCustom} placeholder="Eigener Slogan (überschreibt Vorlage)" className="mt-2 h-11 rounded-lg border-[#E2E8F0] bg-white text-sm focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
        <p className="mt-1 text-right text-[11px] text-[#A0AEC0]" data-testid="slogan-custom-count">{sloganCustom.length}/120</p>
      </div>
      <div>
        <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Weiterer Text unter dem Slogan (optional)</label>
        <Select value={subtext || "__none__"} onValueChange={(v) => { const val = v === "__none__" ? "" : v; setSubtext(val); saveField({ subtext: val }); }}>
          <SelectTrigger data-testid="subtext-dropdown" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue placeholder="Baustein wählen …" /></SelectTrigger>
          <SelectContent position="popper" sideOffset={4} className="max-h-[50vh] max-w-[calc(100vw-3rem)] overflow-y-auto">
            <SelectItem value="__none__" data-testid="subtext-option-none" className="text-sm">— ohne Text —</SelectItem>
            {subtextOpts.map((t, i) => (<SelectItem key={i} value={t} data-testid={`subtext-option-${i}`} className="text-sm">{t.length > 46 ? t.slice(0, 46) + "…" : t}</SelectItem>))}
          </SelectContent>
        </Select>
        <textarea data-testid="subtext-input" rows={3} maxLength={240} value={subtextCustom} onChange={(e) => setSubtextCustom(e.target.value)} onBlur={() => saveField({ subtext_custom: subtextCustom })} placeholder="Eigener Text (überschreibt Vorlage)" className="mt-2 w-full resize-none rounded-lg border border-[#E2E8F0] bg-white p-3 text-sm leading-snug focus:border-[#C5A059] focus:outline-none focus:ring-1 focus:ring-[#C5A059]" />
        <p className="mt-1 text-right text-[11px] text-[#A0AEC0]" data-testid="subtext-count">{subtextCustom.length}/240</p>
      </div>
      <div>
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Live-Vorschau deiner Seite</p>
        <LandingPreview beraterCode={user?.berater_code} praemieAktiv={aktiv} reloadKey={`${aktiv}|${user?.slogan_variante}|${user?.slogan_custom}|${user?.claim}|${user?.claim_custom}|${user?.subtext}|${user?.subtext_custom}|${user?.firma}|${user?.firma_anzeigen}|${user?.anzeige_name}|${user?.zusatz}|${user?.zusatz_anzeigen}|${user?.telefon}|${user?.telefon_anzeigen}|${user?.email}|${user?.email_anzeigen}|${user?.whatsapp}|${user?.whatsapp_anzeigen}|${user?.webseite}|${user?.webseite_anzeigen}|${user?.kontakt_reihenfolge}|${user?.logo_url}|${user?.portrait_url}|${user?.personal_logo_url}|${JSON.stringify(user?.branding_pos)}`} />
      </div>
    </>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="tab-profil">
      <ImageCropDialog
        open={!!cropKind}
        onClose={() => { setCropKind(null); setCropSrc(null); }}
        imageSrc={cropSrc}
        onCropped={uploadCropped}
        title={cropKind === "portrait" ? "Foto zuschneiden" : "Logo zuschneiden"}
      />
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0A1128]">Profil</h1>
        <p className="mt-1 text-sm text-[#718096]">{user?.email}</p>
      </div>

      <div className="space-y-3 rounded-2xl border border-[#E2E8F0] bg-white p-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Meine Angaben</p>
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Name</label>
          <textarea data-testid="profile-name" rows={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} onBlur={() => saveField({ name: form.name })} className="w-full resize-none rounded-lg border border-[#E2E8F0] bg-white p-3 text-sm leading-snug focus:border-[#C5A059] focus:outline-none focus:ring-1 focus:ring-[#C5A059]" />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Zusatz</label>
          <textarea data-testid="profile-zusatz" rows={2} value={form.zusatz || ""} onChange={(e) => setForm({ ...form, zusatz: e.target.value })} onBlur={() => saveField({ zusatz: form.zusatz })} className="w-full resize-none rounded-lg border border-[#E2E8F0] bg-white p-3 text-sm leading-snug focus:border-[#C5A059] focus:outline-none focus:ring-1 focus:ring-[#C5A059]" />
        </div>
        <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="zusatz-anzeigen-label">
          <Checkbox data-testid="zusatz-anzeigen-checkbox" checked={zusatzAnzeigen} onCheckedChange={(v) => { const val = !!v; setZusatzAnzeigen(val); saveField({ zusatz_anzeigen: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
          <span className="text-sm text-[#4A5568]">Zusatz-Zeile unter dem Namen anzeigen (z. B. deine Fachrichtung)</span>
        </label>
        {F("firma", "Firma")}
        <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="firma-anzeigen-label">
          <Checkbox data-testid="firma-anzeigen-checkbox" checked={firmaAnzeigen} onCheckedChange={(v) => { const val = !!v; setFirmaAnzeigen(val); saveField({ firma_anzeigen: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
          <span className="text-sm text-[#4A5568]">Firma auf meiner Empfehlungsseite anzeigen</span>
        </label>
        <div className="mt-1 rounded-lg bg-[#F8F9FA] p-3" data-testid="datev-konten">
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">DATEV-Konten (Steuer-Export)</p>
          <p className="mb-2 text-xs text-[#718096]">Optional – nur für den DATEV-Export. Standard: 4600 (Aufwand) / 1200 (Bank). Bei Bedarf mit dem Steuerbüro abstimmen.</p>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="datev-konto-input" inputMode="numeric" value={form.datev_konto || ""} onChange={(e) => setForm({ ...form, datev_konto: e.target.value })} onBlur={() => saveField({ datev_konto: form.datev_konto || "" })} placeholder="Konto 4600" className="h-10 rounded-lg border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#C5A059] focus:outline-none" />
            <input data-testid="datev-gegenkonto-input" inputMode="numeric" value={form.datev_gegenkonto || ""} onChange={(e) => setForm({ ...form, datev_gegenkonto: e.target.value })} onBlur={() => saveField({ datev_gegenkonto: form.datev_gegenkonto || "" })} placeholder="Gegenkonto 1200" className="h-10 rounded-lg border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#C5A059] focus:outline-none" />
          </div>
        </div>
        <div>
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Telefon</label>
            <Input data-testid="profile-telefon" type="tel" inputMode="tel" autoComplete="tel" value={form.telefon || ""} onChange={(e) => setForm({ ...form, telefon: e.target.value })} onBlur={() => { const f = formatPhone(form.telefon); setForm((p) => ({ ...p, telefon: f })); saveField({ telefon: f }); }} className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          </div>
          <label className="mt-2 flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="telefon-anzeigen-label">
            <Checkbox data-testid="telefon-anzeigen-checkbox" checked={telefonAnzeigen} onCheckedChange={(v) => { const val = !!v; setTelefonAnzeigen(val); saveField({ telefon_anzeigen: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
            <span className="text-sm text-[#4A5568]"><span className="mr-1">☎</span>Funktion „Berater anrufen" aktivieren – erscheint als grüner Anruf-Button auf deiner Startseite (deine Nummer bleibt verborgen)</span>
          </label>
          <div className="mt-2">
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">E-Mail</label>
            <div className="flex h-11 items-center rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] px-3 text-sm text-[#4A5568]" data-testid="profile-email-readonly">{user?.email}</div>
          </div>
          <label className="mt-2 flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="email-anzeigen-label">
            <Checkbox data-testid="email-anzeigen-checkbox" checked={emailAnzeigen} onCheckedChange={(v) => { const val = !!v; setEmailAnzeigen(val); saveField({ email_anzeigen: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
            <span className="text-sm text-[#4A5568]"><span className="mr-1">✉</span>Funktion „Berater schreiben" aktivieren – erscheint als E-Mail-Button auf deiner Startseite (deine Adresse bleibt verborgen)</span>
          </label>
          <div className="mt-2">
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">WhatsApp (mit Ländervorwahl)</label>
            <Input data-testid="profile-whatsapp" type="tel" inputMode="tel" autoComplete="tel" value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} onBlur={() => { const f = formatPhone(form.whatsapp); setForm((p) => ({ ...p, whatsapp: f })); saveField({ whatsapp: f }); }} placeholder="" className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          </div>
          <label className="mt-2 flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="whatsapp-anzeigen-label">
            <Checkbox data-testid="whatsapp-anzeigen-checkbox" checked={whatsappAnzeigen} onCheckedChange={(v) => { const val = !!v; setWhatsappAnzeigen(val); saveField({ whatsapp_anzeigen: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
            <span className="text-sm text-[#4A5568]"><span className="mr-1">💬</span>Funktion „WhatsApp" aktivieren – erscheint als grüner WhatsApp-Button auf deiner Startseite (Nummer mit Ländervorwahl, z. B. +49 …)</span>
          </label>
          <div className="mt-3">
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Eigene Webseite (optional)</label>
            <Input data-testid="profile-webseite" type="url" inputMode="url" value={form.webseite || ""} onChange={(e) => setForm({ ...form, webseite: e.target.value })} onBlur={() => saveField({ webseite: form.webseite })} placeholder="www.deine-seite.de" className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          </div>
          <label className="mt-2 flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="webseite-anzeigen-label">
            <Checkbox data-testid="webseite-anzeigen-checkbox" checked={webseiteAnzeigen} onCheckedChange={(v) => { const val = !!v; setWebseiteAnzeigen(val); saveField({ webseite_anzeigen: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
            <span className="text-sm text-[#4A5568]"><span className="mr-1">🌐</span>Funktion „Webseite" aktivieren – erscheint als Button auf deiner Startseite</span>
          </label>
          <div className="mt-3 rounded-lg bg-[#F8F9FA] p-3" data-testid="kontakt-reihenfolge">
            <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Reihenfolge der Kontakt-Buttons</p>
            <p className="mb-2 text-xs text-[#718096]">Lege fest, in welcher Reihenfolge die aktivierten Buttons auf deiner Startseite erscheinen.</p>
            {kontaktOrder.map((k, i) => (
              <div key={k} className="mb-1.5 flex items-center justify-between rounded-lg border border-[#E2E8F0] bg-white px-3 py-2" data-testid={`kontakt-order-row-${k}`}>
                <span className="text-sm text-[#4A5568]">{KONTAKT_LABELS[k]}</span>
                <div className="flex items-center gap-1">
                  <button type="button" data-testid={`kontakt-up-${k}`} disabled={i === 0} onClick={() => moveKontakt(i, -1)} className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E2E8F0] text-base leading-none text-[#4A5568] disabled:opacity-30">↑</button>
                  <button type="button" data-testid={`kontakt-down-${k}`} disabled={i === kontaktOrder.length - 1} onClick={() => moveKontakt(i, 1)} className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E2E8F0] text-base leading-none text-[#4A5568] disabled:opacity-30">↓</button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Text des Karriere-Knopfs</label>
            <Select value={form.karriere_variante || karriereOptionsFor(user?.branche || "finanz")[0].k} onValueChange={(v) => { setForm({ ...form, karriere_variante: v }); saveField({ karriere_variante: v }); }}>
              <SelectTrigger data-testid="profile-karriere-dropdown" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue /></SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="max-h-[50vh] max-w-[calc(100vw-3rem)] overflow-y-auto">
                {karriereOptionsFor(user?.branche || "finanz").map((o) => (<SelectItem key={o.k} value={o.k} data-testid={`karriere-option-${o.k}`} className="text-sm">{o.t}</SelectItem>))}
              </SelectContent>
            </Select>
            <Input data-testid="profile-karriere-text" value={form.karriere_text || ""} onChange={(e) => setForm({ ...form, karriere_text: e.target.value })} onBlur={() => saveField({ karriere_text: form.karriere_text })} placeholder="Eigener Text (überschreibt Vorlage)" className="mt-2 h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Eigene Datenschutz-URL (optional)</label>
          <Input data-testid="profile-datenschutz_url" type="url" inputMode="url" value={form.datenschutz_url || ""} onChange={(e) => setForm({ ...form, datenschutz_url: e.target.value })} onBlur={() => saveField({ datenschutz_url: form.datenschutz_url })} placeholder="https://…" className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          <p className="mt-1.5 text-[11px] leading-snug text-[#A0AEC0]">Wenn gesetzt, verweist der „Datenschutz"-Link auf deiner Startseite auf diese Adresse (z. B. deine Firmen-Datenschutzseite). Leer lassen = neutrale Standard-Datenschutzseite des Tools.</p>
        </div>
        <p className="pt-1 text-center text-[11px] text-[#A0AEC0]" data-testid="profile-autosave-hint">Änderungen werden automatisch gespeichert.</p>
      </div>

      <div className="space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="branding-setting">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Logo & Foto</p>

        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F1EDE2] ring-1 ring-[#C5A059]/30">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="h-full w-full object-contain p-1" data-testid="logo-thumb" /> : <span className="text-[10px] text-[#A0AEC0]">Logo</span>}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0A1128]">Eigenes Logo</p>
            <p className="text-[11px] text-[#718096]">Ersetzt den Initialen-Kreis auf deiner Seite.</p>
            <div className="mt-1.5 flex gap-2">
              <label data-testid="logo-upload-btn" className="cursor-pointer rounded-full bg-[#0A1128] px-3 py-1.5 text-xs font-semibold text-white active:scale-95">
                {logoUrl ? "Ändern" : "Hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={onPickFile("logo")} />
              </label>
              {logoUrl ? <button data-testid="logo-remove-btn" onClick={() => removeImage("logo")} className="rounded-full border border-[#E2E8F0] px-3 py-1.5 text-xs font-semibold text-[#718096] active:scale-95">Entfernen</button> : null}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F1EDE2] ring-1 ring-[#C5A059]/30">
            {portraitUrl ? <img src={portraitUrl} alt="Foto" className="h-full w-full object-cover" data-testid="portrait-thumb" /> : <span className="text-[10px] text-[#A0AEC0]">Foto</span>}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0A1128]">Foto von dir</p>
            <p className="text-[11px] text-[#718096]">Zuschneiden & zoomen. Wähle unten aus, wo es erscheint.</p>
            <div className="mt-1.5 flex gap-2">
              <label data-testid="portrait-upload-btn" className="cursor-pointer rounded-full bg-[#0A1128] px-3 py-1.5 text-xs font-semibold text-white active:scale-95">
                {portraitUrl ? "Ändern" : "Hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={onPickFile("portrait")} />
              </label>
              {portraitUrl ? <button data-testid="portrait-remove-btn" onClick={() => removeImage("portrait")} className="rounded-full border border-[#E2E8F0] px-3 py-1.5 text-xs font-semibold text-[#718096] active:scale-95">Entfernen</button> : null}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F1EDE2] ring-1 ring-[#C5A059]/30">
            {personalLogoUrl ? <img src={personalLogoUrl} alt="Persönliches Logo" className="h-full w-full object-contain p-1" data-testid="personal-thumb" /> : <span className="text-[10px] text-[#A0AEC0]">Logo 2</span>}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0A1128]">Persönliches Logo</p>
            <p className="text-[11px] text-[#718096]">Zusätzlich zum Firmenlogo – z. B. deine eigene Marke.</p>
            <div className="mt-1.5 flex gap-2">
              <label data-testid="personal-upload-btn" className="cursor-pointer rounded-full bg-[#0A1128] px-3 py-1.5 text-xs font-semibold text-white active:scale-95">
                {personalLogoUrl ? "Ändern" : "Hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={onPickFile("personal")} />
              </label>
              {personalLogoUrl ? <button data-testid="personal-remove-btn" onClick={() => removeImage("personal")} className="rounded-full border border-[#E2E8F0] px-3 py-1.5 text-xs font-semibold text-[#718096] active:scale-95">Entfernen</button> : null}
            </div>
          </div>
        </div>

        <div className="space-y-3" data-testid="branding-positionen">
          <label className="block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Positionen<InfoHint testid="hint-positionen" text="Lege je Fläche (Startseite, Poster, PDF) fest, wo Firmenlogo, persönliches Logo und Foto erscheinen. Jede Position ist nur einmal belegbar." /></label>
          <p className="text-[11px] leading-snug text-[#A0AEC0]">Lege je Fläche fest, wo Firmenlogo, persönliches Logo und Foto erscheinen. Jede Position kann nur einmal belegt werden – wählst du sie erneut, wird sie beim anderen Element automatisch gelöst.</p>
          {[
            { key: "startseite", label: "Startseite", opts: [["aus", "Aus"], ["links", "Kopf links"], ["rechts", "Kopf rechts"], ["karte", "Beraterkarte"]] },
            { key: "poster", label: "Poster", opts: [["aus", "Aus"], ["oben_links", "Oben links"], ["oben_rechts", "Oben rechts"], ["fusszeile", "Fußzeile"]] },
            { key: "pdf", label: "PDF-Beleg", opts: [["aus", "Aus"], ["oben_rechts", "Oben rechts"]] },
          ].map((surf) => (
            <div key={surf.key} className="rounded-lg bg-[#F8F9FA] p-3">
              <p className="mb-2 text-xs font-semibold text-[#4A5568]">{surf.label}</p>
              <div className="space-y-2">
                {[["firmenlogo", "Firmenlogo", logoUrl], ["persoenlich", "Persönliches Logo", personalLogoUrl], ["portrait", "Foto", portraitUrl]].map(([el, elLabel, elUrl]) => (
                  <div key={el} className={`flex items-center justify-between gap-2 ${elUrl ? "" : "opacity-40"}`}>
                    <span className="text-sm text-[#4A5568]">{elLabel}</span>
                    <Select value={brandingPos?.[surf.key]?.[el] || "aus"} onValueChange={(v) => setSlot(surf.key, el, v)} disabled={!elUrl}>
                      <SelectTrigger data-testid={`pos-${surf.key}-${el}`} className="h-9 w-36 rounded-lg border-[#E2E8F0] bg-white text-left text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent position="popper" sideOffset={4}>
                        {surf.opts.map(([ov, ol]) => (<SelectItem key={ov} value={ov} data-testid={`pos-${surf.key}-${el}-${ov}`} className="text-xs">{ol}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F1] p-4" data-testid="praemie-setting">
        <label className="flex cursor-pointer items-start gap-2.5" data-testid="praemie-aktiv-label">
          <Checkbox data-testid="praemie-aktiv-checkbox" checked={praemieAktiv} onCheckedChange={(v) => { const val = !!v; setPraemieAktiv(val); const dv = val ? "praemie" : "vertrauen"; setSloganVariante(dv); savePraemieMode(val, dv); }} className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-[#0A1128]">Geldprämie aktiv</span>
            <span className="block text-xs text-[#718096]">An: Empfehlungen bringen eine Geldprämie. Aus: Empfehlen ganz ohne Geld – nur aus Überzeugung.</span>
          </span>
        </label>

        <p className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-[11px] leading-relaxed text-[#8a6d2f]" data-testid="app-name-info">
          <span className="font-semibold">App-Name:</span> Mit aktiver Geldprämie heißt deine Seite <span className="font-semibold">Prämio</span>, ohne Geldprämie <span className="font-semibold">Empfehlungs-Kompass</span>. Der Name wechselt automatisch mit dieser Einstellung.
        </p>

        {praemieAktiv ? (
          <>
          <div className="mt-4 border-t border-[#C5A059]/20 pt-4" data-testid="praemie-setting">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Prämie pro Kunde<InfoHint testid="hint-praemie" text="Standard-Dankeschön für neue Empfehlungen: entweder ein Geldbetrag oder eine Sachprämie. Pro Empfehlung individuell änderbar." /></p>
            <p className="mb-2 text-xs text-[#718096]">Gilt für neue Empfehlungen – Geldbetrag oder Sachprämie.</p>
            <div className="mb-3 flex items-center gap-1">
              <button type="button" data-testid="reset-praemien-btn" onClick={resetAllPraemien} className="flex-1 rounded-full border border-[#E2E8F0] bg-white py-2 text-xs font-semibold text-[#718096] active:scale-[0.99]">Alle Prämien auf Standard zurücksetzen</button>
              <InfoHint testid="hint-reset-praemien" text="Setzt bei allen noch NICHT freigeschalteten Empfehlungen die Prämie auf deinen aktuellen Profil-Standard (Geld/Sache + Betrag). Bereits freigeschaltete oder ausgezahlte Prämien bleiben unverändert – für die Buchhaltung." />
            </div>
            <div className="mb-2 flex gap-2">
              <button type="button" data-testid="praemie-typ-geld" onClick={() => setPraemieTyp("geld")} className={`h-9 flex-1 rounded-full text-xs font-semibold ${praemieTyp === "geld" ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Geldbetrag</button>
              <button type="button" data-testid="praemie-typ-sache" onClick={() => setPraemieTyp("sache")} className={`h-9 flex-1 rounded-full text-xs font-semibold ${praemieTyp === "sache" ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Sachprämie</button>
            </div>
            {praemieTyp === "sache" ? (
              <div className="space-y-2">
                <Select value={sachOptionen.includes(praemieSache) ? praemieSache : ""} onValueChange={(v) => setPraemieSache(v)}>
                  <SelectTrigger data-testid="praemie-sache-select" className="h-11 rounded-lg border-[#E2E8F0] bg-white text-left text-sm"><SelectValue placeholder="Sachprämie wählen" /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="max-h-[46vh]">
                    {sachOptionen.map((s) => (<SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Input data-testid="praemie-sache-custom" maxLength={80} value={praemieSache} onChange={(e) => setPraemieSache(e.target.value)} placeholder="oder eigene Sachprämie (z. B. Flasche Wein)" className="h-11 rounded-lg border-[#E2E8F0] text-sm" />
                <div className="rounded-lg bg-white/70 p-2.5" data-testid="sach-optionen-manager">
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">Auswahlliste für Sachprämien<InfoHint testid="hint-sach-optionen" text="Diese Liste ist dein Dropdown – und wird, wenn du eine Sachprämie anbietest, auch dem Empfehlenden gezeigt, der sich daraus etwas wünschen kann. Entferne Einträge (×) oder füge eigene hinzu." /></p>
                  <div className="flex flex-wrap gap-1.5">
                    {sachOptionen.map((s) => (
                      <span key={s} data-testid="sach-chip" className="inline-flex items-center gap-1 rounded-full border border-[#C5A059]/40 bg-[#FBF8F1] px-2 py-0.5 text-[11px] text-[#8a6d2f]">{s}<button type="button" data-testid="sach-chip-remove" onClick={() => removeSach(s)} className="text-sm leading-none text-[#C0392B]">×</button></span>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Input data-testid="sach-new-input" maxLength={80} value={sachNew} onChange={(e) => setSachNew(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSach(); } }} placeholder="eigene Sachprämie hinzufügen" className="h-9 flex-1 rounded-lg border-[#E2E8F0] text-sm" />
                    <button type="button" data-testid="sach-add-btn" onClick={addSach} className="h-9 shrink-0 rounded-full bg-[#0A1128] px-3 text-xs font-semibold text-white active:scale-[0.99]">+ Hinzufügen</button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input data-testid="praemie-input" type="number" min="0" value={praemie} onChange={(e) => setPraemie(e.target.value)} className="h-11 w-24 rounded-lg border border-[#E2E8F0] bg-white pl-3 pr-6 text-base font-semibold text-[#0A1128] focus:border-[#C5A059] focus:outline-none" />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-[#718096]">€</span>
                  </div>
                  <span className="text-xs text-[#718096]">Gegenwert (optional, für Buchhaltung)</span>
                </div>
                <button data-testid="praemie-save-btn" onClick={savePraemie} disabled={pBusy} className="h-11 w-full rounded-full bg-[#0A1128] text-sm font-semibold text-white disabled:opacity-60">{pBusy ? "…" : "Speichern"}</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input data-testid="praemie-input" type="number" min="0" value={praemie} onChange={(e) => setPraemie(e.target.value)} className="h-11 w-24 rounded-lg border border-[#E2E8F0] bg-white pl-3 pr-6 text-base font-semibold text-[#0A1128] focus:border-[#C5A059] focus:outline-none" />
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-[#718096]">€</span>
                </div>
                <button data-testid="praemie-save-btn" onClick={savePraemie} disabled={pBusy} className="h-11 shrink-0 rounded-full bg-[#0A1128] px-4 text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60">{pBusy ? "…" : "OK"}</button>
              </div>
            )}
          </div>
          <div className="mt-4 space-y-3 border-t border-[#C5A059]/20 pt-4" data-testid="slogan-select">
            {contentFields(true)}
          </div>
          </>
        ) : (
          <div className="mt-4 space-y-3 border-t border-[#C5A059]/20 pt-4" data-testid="slogan-select">
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Prämienhöhe für Ausnahmen</label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input data-testid="praemie-input" type="number" min="0" value={praemie} onChange={(e) => setPraemie(e.target.value)} className="h-11 w-24 rounded-lg border border-[#E2E8F0] bg-white pl-3 pr-6 text-base font-semibold text-[#0A1128] focus:border-[#C5A059] focus:outline-none" />
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-[#718096]">€</span>
                </div>
                <button data-testid="praemie-save-btn" onClick={savePraemie} disabled={pBusy} className="h-11 shrink-0 rounded-full bg-[#0A1128] px-4 text-sm font-semibold text-white disabled:opacity-60">{pBusy ? "…" : "OK"}</button>
              </div>
              <p className="mt-1.5 text-[11px] leading-snug text-[#A0AEC0]">Wird verwendet, wenn du einer Empfehlung ausnahmsweise eine Prämie gibst (QR „Mit Prämie" oder „+ Prämie hinzufügen").</p>
            </div>
            {contentFields(false)}
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-2xl border border-[#E2E8F0] bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#0A1128]">Benachrichtigungen</p>
            <p className="text-xs text-[#718096]">Push auf diesem Gerät.</p>
          </div>
          <PushToggle />
        </div>
        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="themen-anzeigen-label">
          <Checkbox data-testid="themen-anzeigen-checkbox" checked={themenAn} onCheckedChange={(v) => { const val = !!v; setThemenAn(val); saveField({ themen_anzeigen: val }); }} className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
          <span className="min-w-0">
            <span className="block text-sm text-[#4A5568]">Themen-Vorschläge im Empfehlungsformular zeigen</span>
            <span className="block text-xs text-[#A0AEC0]">Der Empfehlende kann optional antippen, worum es geht (z. B. Kontoführungsgebühren, Altersvorsorge).</span>
          </span>
        </label>
        {themenAn && (
          <div className="rounded-lg bg-[#F8F9FA] px-3 py-3" data-testid="themen-custom-block">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Eigene Themen (optional)</p>
            <p className="mb-2 text-xs text-[#A0AEC0]">Bis zu 5 eigene Schwerpunkte – erscheinen zusätzlich als Chip im Formular.</p>
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Input
                  key={i}
                  data-testid={`themen-custom-input-${i}`}
                  maxLength={80}
                  value={themenCustom[i] || ""}
                  onChange={(e) => { const arr = [...themenCustom]; arr[i] = e.target.value; setThemenCustom(arr); }}
                  onBlur={() => saveThemenCustom(themenCustom)}
                  placeholder={`Eigenes Thema ${i + 1}`}
                  className="h-10 w-full rounded-lg border-[#E2E8F0] bg-white text-sm focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                />
              ))}
            </div>
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="notif-autoclean-label">
          <Checkbox data-testid="notif-autoclean-checkbox" checked={notifClean} onCheckedChange={(v) => { const val = !!v; setNotifClean(val); saveField({ notif_autoclean: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
          <span className="text-sm text-[#4A5568]">Gelesene Benachrichtigungen nach 30 Tagen automatisch entfernen</span>
        </label>
        <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-[#F8F9FA] px-3 py-2.5" data-testid="autodelete-label">
          <Checkbox data-testid="autodelete-checkbox" checked={autodelUn} onCheckedChange={(v) => { const val = !!v; setAutodelUn(val); saveField({ autodelete_unbestaetigt: val }); }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
          <span className="text-sm text-[#4A5568]">Nicht bestätigte Empfehlungen automatisch löschen</span>
        </label>
        {autodelUn && (
          <div className="flex flex-wrap items-center gap-2 pl-3" data-testid="autodelete-tage-row">
            <span className="text-sm text-[#4A5568]">Nach</span>
            <Input data-testid="autodelete-tage" type="number" min={3} max={90} value={autodelTage} onChange={(e) => setAutodelTage(e.target.value)} onBlur={() => saveField({ autodelete_tage: Number(autodelTage) })} className="h-9 w-16 rounded-lg border-[#E2E8F0] text-center" />
            <span className="text-sm text-[#4A5568]">Tagen – vorher Push-Erinnerung (erneute Mail / Werber kontaktieren).</span>
          </div>
        )}
      </div>

      <KarriereDialog />

      <JournalDialog advisorName={user?.name} advisorEmail={user?.email} />

      {user?.is_owner && <OwnerStats />}
      {user?.is_admin && <AdminDialog />}

      {user?.billing?.pflichtig && <LizenzCard user={user} refresh={refresh} />}
      {user?.billing?.pflichtig && <AboCard />}

      <div className="space-y-2.5 rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="konto-actions">
        <p className="text-sm font-semibold text-[#0A1128]">Konto &amp; Zurücksetzen</p>
        <div className="flex items-center gap-1">
          <button type="button" data-testid="reset-defaults-btn" onClick={resetDefaults} className="flex-1 rounded-full border border-[#E2E8F0] bg-white py-2.5 text-sm font-semibold text-[#718096] active:scale-[0.99]">Standard wiederherstellen</button>
          <InfoHint testid="hint-reset-defaults" text="Setzt nur deine Anzeige-, Text- und Branding-Einstellungen (Slogans, Positionen, Themen, Prämien-Standard) hier im Profil auf die Werkseinstellung zurück. Empfehlungen, Auszahlungen und ausgezahlte Prämien bleiben unberührt." />
        </div>
        <div className="flex items-center gap-1">
          <button type="button" data-testid="account-delete-btn" onClick={deleteAccount} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#C0392B]/40 bg-white py-2.5 text-sm font-semibold text-[#C0392B] active:scale-[0.99]"><Trash size={15} /> Account löschen</button>
          <InfoHint testid="hint-account-delete" text="Löscht dein Konto dauerhaft: du kannst dich danach nicht mehr anmelden und deine Profildaten werden entfernt. Bereits ausgezahlte Prämien und Steuerbelege bleiben aus Aufbewahrungsgründen anonymisiert gespeichert. Vor dem Löschen wird noch einmal nachgefragt." />
        </div>
      </div>

      <button
        data-testid="logout-btn"
        onClick={logout}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#E2E8F0] bg-white text-sm font-semibold text-[#718096] transition-colors hover:bg-[#F1F5F9]"
      >
        <SignOut size={18} /> Abmelden
      </button>

      <div className="mt-2 rounded-2xl border border-[#C5A059]/25 bg-[#FBF8F1] p-4 text-center" data-testid="donate-card">
        <p className="text-sm font-semibold text-[#0A1128]">Gefällt dir die App?</p>
        <p className="mt-1 text-xs leading-relaxed text-[#718096]">Unterstütze die Weiterentwicklung mit einer kleinen Spende – in PayPal einfach „Freunde und Bekannte" wählen. Und sei nicht so geizig 😉</p>
        <a
          href={DONATE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="donate-btn"
          className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-sm font-semibold text-white transition-all active:scale-[0.99]"
        >
          <Heart size={17} weight="fill" color="#C5A059" /> Entwickler per PayPal unterstützen
        </a>
        <p className="mt-2 text-[11px] leading-relaxed text-[#718096]">Die App läuft schon rund – hier kannst du persönliche Änderungswünsche schicken.</p>
        <a
          href={`mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent("Änderungsvorschlag")}`}
          data-testid="feedback-btn"
          className="mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[#C5A059] bg-white text-sm font-semibold text-[#8a6d2f] transition-all active:scale-[0.99]"
        >
          <EnvelopeSimple size={17} weight="fill" color="#C5A059" /> Änderungsvorschläge senden
        </a>
      </div>
    </motion.div>
  );
}


function FirmaAdminOnboarding({ user }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (user?.admin_branche && user?.id && !localStorage.getItem(`firma_admin_onboarded_${user.id}`)) {
      setOpen(true);
    }
  }, [user?.admin_branche, user?.id]);
  const dismiss = () => {
    if (user?.id) localStorage.setItem(`firma_admin_onboarded_${user.id}`, "1");
    setOpen(false);
  };
  const step = "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#C5A059]/15 text-xs font-bold text-[#8a6d2f]";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="firma-admin-onboarding">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">Willkommen als Firmen-Admin</DialogTitle>
          <DialogDescription>So lädst du deine Berater ein und behältst den Überblick:</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-[#4A5568]">
          <li className="flex gap-3"><span className={step}>1</span><span>Öffne im Reiter <strong>Profil</strong> den Button <strong>„Berater verwalten"</strong>.</span></li>
          <li className="flex gap-3"><span className={step}>2</span><span>Erstelle unter <strong>Einladungscodes</strong> einen Code und teile ihn mit deinen Beratern – sie landen automatisch in <strong>deiner Firma</strong>.</span></li>
          <li className="flex gap-3"><span className={step}>3</span><span>Ganz oben siehst du jederzeit die <strong>Übersicht deiner Firma</strong>: Berater, Empfehlungen, Abschlussquote und ausgezahlte Prämien.</span></li>
        </ol>
        <button onClick={dismiss} data-testid="onboarding-dismiss" className="mt-2 h-11 w-full rounded-xl bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.99]">Verstanden</button>
      </DialogContent>
    </Dialog>
  );
}

function AboCard() {
  const [b, setB] = useState(null);
  const load = () => api.get("/billing/me").then((r) => setB(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openPortal = async () => { try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; } catch { toast.error("Abo-Portal konnte nicht geöffnet werden."); } };
  const reactivate = async () => { try { await api.post("/billing/reactivate"); toast.success("Kündigung zurückgenommen – dein Abo läuft weiter."); load(); } catch { toast.error("Konnte Kündigung nicht zurücknehmen."); } };
  if (!b) return null;
  const fmt = (d) => new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  const end = b.laeuft_bis || b.bis;
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="abo-card">
      <p className="text-sm font-semibold text-[#0A1128]">Dein Abo</p>
      <p className="mt-1 text-xs leading-relaxed text-[#718096]" data-testid="abo-status">
        {b.gekuendigt
          ? `Gekündigt – dein Zugang läuft noch bis ${end ? fmt(end) : "Periodenende"}, danach endet er.`
          : b.aktiv
            ? `Aktiv – 9,90 €/Monat${end ? `, nächste Abbuchung um den ${fmt(end)}` : ""}.`
            : "Zurzeit kein aktives Abo."}
      </p>
      {b.gekuendigt && (
        <button data-testid="abo-reactivate-btn" onClick={reactivate} className="mt-3 h-11 w-full rounded-full bg-[#276749] text-sm font-semibold text-white transition-all active:scale-[0.99]">Kündigung zurücknehmen</button>
      )}
      <button data-testid="abo-portal-btn" onClick={openPortal} className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[#C5A059] bg-[#FBF8F1] text-sm font-semibold text-[#8a6d2f] transition-colors hover:bg-[#F5EEDD] active:scale-[0.99]">Abo verwalten / kündigen</button>
    </div>
  );
}

function LizenzCard({ user, refresh }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const aktiv = !!user?.billing?.lizenz;
  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/lizenz/einloesen", { code: code.trim() });
      await refresh();
      toast.success(data.typ === "lifetime" ? "Voll-Lizenz aktiviert – dauerhaft freigeschaltet." : "Code eingelöst – Zugang verlängert.");
      setCode("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  if (aktiv) {
    return (
      <div className="rounded-2xl border border-[#276749]/30 bg-[#F0FFF4] p-4" data-testid="lizenz-card-aktiv">
        <p className="flex items-center gap-2 text-sm font-semibold text-[#276749]"><Key size={17} weight="fill" /> Voll-Lizenz aktiv</p>
        <p className="mt-1 text-xs leading-relaxed text-[#4A5568]">Deine App ist dauerhaft freigeschaltet – kein Abo nötig. Vielen Dank für deinen Kauf!</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F1] p-4" data-testid="lizenz-card">
      <p className="flex items-center gap-2 text-sm font-semibold text-[#0A1128]"><Key size={17} weight="fill" color="#C5A059" /> Voll-Lizenz – einmalig kaufen<InfoHint testid="hint-lizenz" text="Die App ist 30 Tage kostenlos. Danach entweder 9,90 €/Monat im Abo ODER einmalig kaufen und dauerhaft freischalten. Nach der PayPal-Zahlung bekommst du einen Freischalt-Code, den du hier unten einlöst." /></p>
      <p className="mt-1 text-xs leading-relaxed text-[#718096]">30 Tage kostenlos testen – danach dauerhaft freischalten statt monatlich zu zahlen. Nach der PayPal-Zahlung erhältst du deinen persönlichen Freischalt-Code.</p>
      <a href={LIZENZ_URL} target="_blank" rel="noopener noreferrer" data-testid="lizenz-buy-btn" className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-sm font-semibold text-white transition-all active:scale-[0.99]">Voll-Lizenz per PayPal erwerben</a>
      <div className="mt-3">
        <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Freischalt-Code einlösen</label>
        <div className="flex gap-2">
          <Input data-testid="lizenz-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="z. B. A1B2C3D4" className="h-11 flex-1 rounded-lg border-[#E2E8F0] bg-white font-mono text-sm uppercase tracking-widest" />
          <button data-testid="lizenz-redeem-btn" onClick={redeem} disabled={busy} className="h-11 shrink-0 rounded-full bg-[#C5A059] px-4 text-sm font-semibold text-[#0A1128] active:scale-[0.99] disabled:opacity-60">{busy ? "…" : "Einlösen"}</button>
        </div>
      </div>
    </div>
  );
}

function OwnerStats() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [openF, setOpenF] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const doSearch = (v) => { setQ(v); if (v.trim().length >= 2) { api.get(`/owner/search?q=${encodeURIComponent(v.trim())}`).then((r) => setResults(r.data.results || [])).catch(() => {}); } else { setResults([]); } };
  const grant = async (id, payload) => { try { await api.post(`/owner/berater/${id}/grant`, payload); toast.success("Gespeichert."); doSearch(q); } catch { toast.error("Aktion fehlgeschlagen."); } };
  useEffect(() => { api.get("/owner/billing-stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const fmt = (d) => new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "short" });
  return (
    <div className="rounded-2xl border border-[#C5A059]/30 bg-[#FBF8F1] p-4" data-testid="owner-stats">
      <p className="text-sm font-semibold text-[#0A1128]">Einnahmen-Übersicht</p>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
        <div><p className="text-2xl font-bold text-[#0A1128]" data-testid="owner-aktive">{s.aktive_zahler}</p><p className="text-[10px] uppercase tracking-wide text-[#A0AEC0]">Zahler</p></div>
        <div><p className="text-2xl font-bold text-[#C5A059]" data-testid="owner-umsatz">{Number(s.monatsumsatz).toFixed(2)} €</p><p className="text-[10px] uppercase tracking-wide text-[#A0AEC0]">pro Monat</p></div>
        <button data-testid="owner-auslaufend-btn" onClick={() => setOpen((o) => !o)} className="rounded-lg py-0.5 transition-colors hover:bg-[#C5A059]/10"><p className="text-2xl font-bold text-[#DD6B20]" data-testid="owner-auslaufend">{s.bald_auslaufend}</p><p className="text-[10px] uppercase tracking-wide text-[#A0AEC0]">läuft bald aus ▾</p></button>
      </div>
      {open && s.bald_auslaufend > 0 && (
        <div className="mt-3 space-y-1 border-t border-[#C5A059]/20 pt-2" data-testid="owner-auslaufend-liste">
          {s.auslaufend_liste.map((p, i) => (
            <div key={i} className="flex items-center justify-between text-xs" data-testid="owner-auslaufend-item">
              <span className="truncate text-[#4A5568]">{p.name}</span>
              <span className="shrink-0 font-semibold text-[#DD6B20]">{p.bis ? fmt(p.bis) : "–"}</span>
            </div>
          ))}
        </div>
      )}
      {(s.firmen || []).length > 0 && (
        <button data-testid="owner-firmen-btn" onClick={() => setOpenF((o) => !o)} className="mt-3 w-full rounded-lg bg-white px-3 py-2 text-left text-xs font-semibold text-[#8a6d2f]">Umsatz je Firma {openF ? "▴" : "▾"}</button>
      )}
      {openF && (
        <div className="mt-2 space-y-1" data-testid="owner-firmen-liste">
          {(s.firmen || []).map((f, i) => (
            <div key={i} className="flex items-center justify-between text-xs" data-testid="owner-firma-item">
              <span className="truncate text-[#4A5568]">{f.firma} <span className="text-[#A0AEC0]">({f.zahler})</span></span>
              <span className="shrink-0 font-semibold text-[#C5A059]">{Number(f.umsatz).toFixed(2)} €</span>
            </div>
          ))}
          <div className="mt-1 border-t border-[#C5A059]/20 pt-1 text-[10px] uppercase tracking-wide text-[#A0AEC0]">nach Branche</div>
          {(s.branchen || []).map((br, i) => (
            <div key={`b${i}`} className="flex items-center justify-between text-xs" data-testid="owner-branche-item">
              <span className="truncate text-[#4A5568]">{br.branche} <span className="text-[#A0AEC0]">({br.zahler})</span></span>
              <span className="shrink-0 font-semibold text-[#C5A059]">{Number(br.umsatz).toFixed(2)} €</span>
            </div>
          ))}
        </div>
      )}
      {s.sammelabos > 0 && <p className="mt-2 text-xs text-[#718096]">{s.sammelabos} Firmen-Sammelabo(s) aktiv</p>}
      <div className="mt-3 border-t border-[#C5A059]/20 pt-3">
        <input data-testid="owner-search-input" value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Berater oder Firma suchen…" className="h-10 w-full rounded-lg border border-[#E2E8F0] bg-white px-3 text-sm outline-none focus:border-[#C5A059]" />
        {results.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="owner-search-results">
            {results.map((r, i) => (
              <div key={i} className="rounded-lg bg-white px-2.5 py-2 text-xs" data-testid="owner-search-item">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0"><span className="block truncate font-semibold text-[#0A1128]">{r.name}</span><span className="block truncate text-[#A0AEC0]">{r.firma || "—"} · {r.branche}{r.bis ? ` · bis ${new Date(r.bis).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}` : ""}</span></span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.is_admin ? "bg-[#0A1128] text-white" : r.aktiv ? "bg-[#276749] text-white" : r.pflichtig ? "bg-[#DD6B20] text-white" : "bg-[#E2E8F0] text-[#4A5568]"}`}>{r.is_admin ? "Admin" : r.aktiv ? "Aktiv" : r.pflichtig ? "Offen" : "Frei"}</span>
                </div>
                {!r.is_admin && (
                  <div className="mt-1.5 space-y-1.5">
                    <button data-testid="owner-grant-befreit" onClick={() => grant(r.id, { befreit: !r.befreit })} className="w-full rounded-full border border-[#C5A059] bg-[#FBF8F1] px-2 py-1 text-[11px] font-semibold text-[#8a6d2f]">{r.befreit ? "Gebühr aktivieren" : "Gebührenfrei stellen"}</button>
                    <div className="flex gap-1.5">
                      {[7, 30, 90].map((n) => (
                        <button key={n} data-testid={`owner-grant-freitage-${n}`} onClick={() => grant(r.id, { frei_tage: n })} className="flex-1 rounded-full border border-[#276749] bg-[#F0FFF4] px-2 py-1 text-[11px] font-semibold text-[#276749]">+{n} Tage</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdminDialog() {
  const { user } = useAuth();
  const isFirmaAdmin = !!user?.admin_branche;
  const [firma, setFirma] = useState(null);
  const [firmaForm, setFirmaForm] = useState({ firma_name: "", praemie_betrag: "", firma_webseite: "", karriere_aktiv: false, karriere_text: "", firma_sammelabo: false });
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState([]);
  const [branchen, setBranchen] = useState([]);
  const [inviteForm, setInviteForm] = useState({ days: "", max_uses: "", branche: "finanz", rolle: "berater", frei_tage: "", lizenz: false });
  const [lizCodes, setLizCodes] = useState([]);
  const [lizForm, setLizForm] = useState({ typ: "lifetime", days: "", max_uses: "", note: "" });

  const load = async () => {
    try {
      const [b, c, br, f, lz] = await Promise.all([api.get("/admin/berater"), api.get("/admin/invite-codes"), api.get("/branchen"), api.get("/admin/firma"), api.get("/admin/lizenz-codes")]);
      setList(b.data);
      setCodes(c.data);
      setBranchen(br.data);
      setFirma(f.data);
      setLizCodes(lz.data);
      if (f.data?.is_firma_admin) setFirmaForm({ firma_name: f.data.firma_name || "", praemie_betrag: String(f.data.praemie_betrag || ""), firma_webseite: f.data.firma_webseite || "", karriere_aktiv: !!f.data.karriere_aktiv, karriere_text: f.data.karriere_text || "", firma_sammelabo: !!f.data.firma_sammelabo });
    } catch {
      /* ignore */
    }
  };

  const createCode = async () => {
    try {
      const { data } = await api.post("/admin/invite-codes", {
        days: inviteForm.days ? Number(inviteForm.days) : null,
        max_uses: inviteForm.max_uses ? Number(inviteForm.max_uses) : null,
        branche: inviteForm.branche,
        rolle: inviteForm.rolle,
        frei_tage: inviteForm.frei_tage ? Number(inviteForm.frei_tage) : null,
        lizenz: inviteForm.lizenz,
      });
      toast.success(`Code ${data.code} erstellt.`);
      setInviteForm({ days: "", max_uses: "", branche: inviteForm.branche, rolle: "berater", frei_tage: "", lizenz: false });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const saveFirma = async () => {    try {
      await api.patch("/admin/firma", {
        firma_name: firmaForm.firma_name,
        praemie_betrag: firmaForm.praemie_betrag ? Number(firmaForm.praemie_betrag) : null,
        firma_webseite: firmaForm.firma_webseite,
        karriere_aktiv: firmaForm.karriere_aktiv,
        karriere_text: firmaForm.karriere_text,
      });
      toast.success("Firma gespeichert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const updateBeraterPraemie = async (id, betrag) => {
    try {
      await api.patch(`/admin/berater/${id}`, { praemie_betrag: betrag });
      toast.success("Prämie aktualisiert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeBerater = async (id) => {
    try {
      await api.delete(`/admin/berater/${id}`);
      toast.success("Berater entfernt.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const updateBeraterAktiv = async (id, aktiv) => {
    try {
      await api.patch(`/admin/berater/${id}`, { aktiv });
      toast.success(aktiv ? "Berater aktiviert." : "Berater deaktiviert – Link inaktiv.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const grantBerater = async (id, payload) => {
    try {
      await api.patch(`/admin/berater/${id}`, payload);
      toast.success("Gespeichert.");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const createLizCode = async () => {
    try {
      const { data } = await api.post("/admin/lizenz-codes", {
        typ: lizForm.typ,
        days: lizForm.days ? Number(lizForm.days) : null,
        max_uses: lizForm.max_uses ? Number(lizForm.max_uses) : null,
        note: lizForm.note,
      });
      toast.success(`Lizenz-Code ${data.code} erstellt.`);
      setLizForm({ typ: "lifetime", days: "", max_uses: "", note: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteLizCode = async (id) => {
    try {
      await api.delete(`/admin/lizenz-codes/${id}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };


  const deleteCode = async (id) => {    try {
      await api.delete(`/admin/invite-codes/${id}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/berater", form);
      toast.success(`Berater ${form.email} angelegt.`);
      setForm({ email: "", name: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <button data-testid="admin-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-sm font-semibold text-white transition-all active:scale-[0.98]">
          <UsersThree size={18} weight="fill" color="#C5A059" /> Berater/Nutzer verwalten
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto rounded-2xl" data-testid="admin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">Berater/Nutzer verwalten</DialogTitle>
          <DialogDescription>{isFirmaAdmin ? "Lade Berater deiner Firma ein und behalte den Überblick." : "Neuer Berater erhält eigenen QR-Link."}</DialogDescription>
        </DialogHeader>

        {firma?.is_firma_admin && (
          <div className="space-y-3 rounded-xl bg-[#FBF8F1] p-4" data-testid="firma-settings">
            <p className="text-sm font-semibold text-[#0A1128]">Firma</p>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Firmen-Name</label>
              <Input data-testid="firma-name-input" value={firmaForm.firma_name} onChange={(e) => setFirmaForm({ ...firmaForm, firma_name: e.target.value })} placeholder={firma.branche_label} className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Firmen-Webseite (für inaktive Links)</label>
              <Input data-testid="firma-webseite-input" type="url" inputMode="url" value={firmaForm.firma_webseite} onChange={(e) => setFirmaForm({ ...firmaForm, firma_webseite: e.target.value })} placeholder="www.firma.de" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Standard-Prämie für alle Berater (€)</label>
              <Input data-testid="firma-praemie-input" type="number" min="1" value={firmaForm.praemie_betrag} onChange={(e) => setFirmaForm({ ...firmaForm, praemie_betrag: e.target.value })} className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-white px-3 py-2.5" data-testid="firma-sammelabo-label">
              <Checkbox data-testid="firma-sammelabo-checkbox" checked={firmaForm.firma_sammelabo} onCheckedChange={async (v) => { const val = !!v; setFirmaForm({ ...firmaForm, firma_sammelabo: val }); try { await api.patch("/admin/firma", { firma_sammelabo: val }); toast.success("Gespeichert."); } catch { toast.error("Fehler beim Speichern."); } }} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
              <span className="text-sm text-[#4A5568]"><span className="mr-1">🏢</span>Sammelabo – ich zahle den Zugang fürs ganze Team (9,90 € je Berater/Monat). Meine Berater müssen dann nicht einzeln zahlen.</span>
            </label>
            {firmaForm.firma_sammelabo && (
              <button data-testid="firma-teamabo-btn" onClick={async () => { try { const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, team: true }); window.location.href = data.checkout_url; } catch { toast.error("Konnte Team-Abo nicht starten."); } }} className="h-10 w-full rounded-full bg-[#C5A059] text-sm font-semibold text-[#0A1128] active:scale-[0.99]">Team-Abo starten / bezahlen</button>
            )}
            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg bg-white px-3 py-2.5" data-testid="firma-karriere-label">
              <Checkbox data-testid="firma-karriere-checkbox" checked={firmaForm.karriere_aktiv} onCheckedChange={(v) => setFirmaForm({ ...firmaForm, karriere_aktiv: !!v })} className="h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
              <span className="text-sm text-[#4A5568]"><span className="mr-1">💼</span>Karriere-Knopf zeigen (Mitarbeiter gesucht) – erscheint auf den Empfehlungsseiten deiner Berater</span>
            </label>
            {firmaForm.karriere_aktiv && (
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Text des Karriere-Knopfs</label>
                <Input data-testid="firma-karriere-text-input" value={firmaForm.karriere_text} onChange={(e) => setFirmaForm({ ...firmaForm, karriere_text: e.target.value })} placeholder="z. B. Wir suchen Verstärkung!" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
              </div>
            )}
            <button data-testid="firma-save-btn" onClick={saveFirma} className="h-10 w-full rounded-full bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.98]">Firma speichern</button>
          </div>
        )}

        {list.length > 0 && (() => {
          const totEmpf = list.reduce((s, b) => s + (b.anzahl_empfehlungen || 0), 0);
          const totKunden = list.reduce((s, b) => s + (b.kunden || 0), 0);
          const totAus = list.reduce((s, b) => s + (b.ausgezahlte_praemien || 0), 0);
          const quote = totEmpf ? Math.round((totKunden / totEmpf) * 100) : 0;
          return (
            <div className="mb-1 rounded-xl bg-[#0A1128] p-4 text-white" data-testid="firma-stats">
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">{firma?.firma_name || (isFirmaAdmin ? "Deine Firma" : "Alle Berater")} · Übersicht</p>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                <div data-testid="firma-stat-berater"><p className="font-display text-xl leading-none">{list.length}</p><p className="mt-1 text-[9px] uppercase tracking-wide text-[#9aa3b2]">Berater</p></div>
                <div data-testid="firma-stat-empf"><p className="font-display text-xl leading-none">{totEmpf}</p><p className="mt-1 text-[9px] uppercase tracking-wide text-[#9aa3b2]">Empf.</p></div>
                <div data-testid="firma-stat-quote"><p className="font-display text-xl leading-none">{quote}%</p><p className="mt-1 text-[9px] uppercase tracking-wide text-[#9aa3b2]">Abschluss</p></div>
                <div data-testid="firma-stat-ausgezahlt"><p className="font-display text-xl leading-none text-[#C5A059]">{totAus} €</p><p className="mt-1 text-[9px] uppercase tracking-wide text-[#9aa3b2]">Ausgez.</p></div>
              </div>
            </div>
          );
        })()}

        <form onSubmit={add} className="space-y-3 rounded-xl bg-[#F8F9FA] p-4">
          <Input data-testid="admin-berater-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          <Input data-testid="admin-berater-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="berater@beispiel.de" className="h-11 rounded-lg border-[#E2E8F0] bg-white focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
          <button data-testid="admin-add-berater-btn" type="submit" disabled={busy} className="flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-[#0A1128] text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60">
            <Plus size={16} weight="bold" /> {busy ? "…" : "Berater anlegen"}
          </button>
        </form>

        <div className="max-h-64 space-y-2 overflow-y-auto pt-1">
          {list.map((b) => (
            <div key={b.id} className="rounded-lg border border-[#E2E8F0] p-3" data-testid="admin-berater-row">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[#0A1128]">{b.name || b.email}</p>
                  <p className="truncate text-xs text-[#718096]">{b.email}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {b.aktiv === false && <span className="rounded-full bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C0392B]" data-testid="berater-inaktiv-badge">Inaktiv</span>}
                  {b.branche && <span className="rounded-full bg-[#C5A059]/10 px-2 py-0.5 text-[10px] font-semibold text-[#8a6d2f]">{b.firma_name || branchen.find((x) => x.key === b.branche)?.label || b.branche}</span>}
                  {b.is_admin && <span className="rounded-full bg-[#0A1128] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">{b.admin_branche ? "Firmen-Admin" : "Admin"}</span>}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-[#F1F5F9] pt-3 text-center">
                <div data-testid="stat-empfehlungen">
                  <p className="font-display text-lg leading-none text-[#0A1128]">{b.anzahl_empfehlungen ?? 0}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-[#A0AEC0]">Empf.</p>
                </div>
                <div data-testid="stat-quote">
                  <p className="font-display text-lg leading-none text-[#0A1128]">{b.abschlussquote ?? 0}%</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-[#A0AEC0]">Abschluss</p>
                </div>
                <div data-testid="stat-ausgezahlt">
                  <p className="font-display text-lg leading-none text-[#8a6d2f]">{b.ausgezahlte_praemien ?? 0} €</p>
                  <p className="mt-1 text-[10px] uppercase tracking-wide text-[#A0AEC0]">Ausgezahlt</p>
                </div>
              </div>
              {b.email !== user?.email && (
                <div className="mt-3 space-y-2 border-t border-[#F1F5F9] pt-3">
                  <PraemieBetragDialog betrag={b.praemie_betrag} moneyOnly onConfirm={(p) => updateBeraterPraemie(b.id, p.betrag)}
                    trigger={<button type="button" data-testid="berater-praemie-btn" className="flex h-9 w-full items-center justify-center gap-1 rounded-full border border-[#C5A059] bg-[#FBF8F1] text-xs font-semibold text-[#8a6d2f] active:scale-[0.99]">Prämie: {b.praemie_betrag} €</button>} />
                  <div className="flex gap-2">
                    <button type="button" data-testid="berater-aktiv-btn" onClick={() => updateBeraterAktiv(b.id, b.aktiv === false)} className={`flex h-9 flex-1 items-center justify-center gap-1 rounded-full border text-xs font-semibold active:scale-[0.99] ${b.aktiv === false ? "border-[#276749]/40 bg-white text-[#276749]" : "border-[#E2E8F0] bg-white text-[#718096]"}`}>{b.aktiv === false ? "Aktivieren" : "Deaktivieren"}</button>
                    <ConfirmDelete name={b.name || b.email} testid="berater-delete-btn" label="Entfernen" onConfirm={() => removeBerater(b.id)} className="flex h-9 flex-1 items-center justify-center gap-1 rounded-full border border-[#C0392B]/40 bg-white text-xs font-semibold text-[#C0392B] active:scale-[0.99]" />
                  </div>
                  <div className="rounded-lg bg-[#FBF8F1] p-2" data-testid="berater-lizenz-box">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-[#8a6d2f]" data-testid="berater-lizenz-status">Nutzungsdauer / Lizenz{b.lizenz ? " · Voll-Lizenz" : b.abo_aktiv && b.abo_bis ? ` · bis ${new Date(b.abo_bis).toLocaleDateString("de-DE")}` : b.gebuehr_befreit ? " · gebührenfrei" : ""}</p>
                      <InfoHint testid="hint-admin-lizenz" text="Lege fest, wie lange dieser Nutzer Zugang hat: verlängere um Tage, schalte eine dauerhafte Voll-Lizenz frei (kein Abo nötig) oder nimm sie wieder zurück." />
                    </div>
                    <div className="mt-1.5 flex gap-1.5">
                      {[30, 90, 365].map((n) => (
                        <button key={n} type="button" data-testid={`admin-freitage-${n}`} onClick={() => grantBerater(b.id, { frei_tage: n })} className="flex-1 rounded-full border border-[#276749]/40 bg-white py-1 text-[11px] font-semibold text-[#276749] active:scale-[0.99]">+{n}T</button>
                      ))}
                      <button type="button" data-testid="admin-lizenz-toggle" onClick={() => grantBerater(b.id, { lizenz: !b.lizenz })} className={`flex-1 rounded-full border py-1 text-[11px] font-semibold active:scale-[0.99] ${b.lizenz ? "border-[#C0392B]/40 bg-white text-[#C0392B]" : "border-[#C5A059] bg-[#FBF8F1] text-[#8a6d2f]"}`}>{b.lizenz ? "Lizenz aus" : "Voll-Lizenz"}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-2 space-y-3 rounded-xl bg-[#F8F9FA] p-4" data-testid="invite-section">
          <div className="flex items-center gap-2">
            <Ticket size={18} weight="duotone" color="#C5A059" />
            <p className="text-sm font-semibold text-[#0A1128]">Einladungs- / Gutscheincodes</p>
          </div>
          <p className="text-xs text-[#718096]">Neue Berater brauchen zum Registrieren einen dieser Codes. Leer lassen = unbegrenzt / ohne Ablauf.</p>
          {isFirmaAdmin ? (
            <p className="text-xs text-[#4A5568]" data-testid="invite-branche-locked">Codes gelten für deine Firma: <span className="font-semibold text-[#8a6d2f]">{firma?.firma_name || branchen.find((x) => x.key === user.admin_branche)?.label || user.admin_branche}</span></p>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Branche des Codes</label>
                <Select value={inviteForm.branche} onValueChange={(v) => setInviteForm({ ...inviteForm, branche: v })}>
                  <SelectTrigger data-testid="invite-branche" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {branchen.map((br) => (<SelectItem key={br.key} value={br.key} data-testid={`invite-branche-${br.key}`}>{br.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-xs font-medium text-[#4A5568]" data-testid="invite-admin-toggle">
                <input type="checkbox" checked={inviteForm.rolle === "admin"} onChange={(e) => setInviteForm({ ...inviteForm, rolle: e.target.checked ? "admin" : "berater" })} className="h-4 w-4 accent-[#C5A059]" />
                Als Firmen-Admin einladen (verwaltet eigene Berater dieser Branche)
              </label>
            </>
          )}
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Gratis-Tage (kostenlose Testphase)</label>
            <Input data-testid="invite-freitage" type="number" min="0" value={inviteForm.frei_tage} onChange={(e) => setInviteForm({ ...inviteForm, frei_tage: e.target.value })} placeholder="z. B. 14 – leer = keine" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-xs font-medium text-[#4A5568]" data-testid="invite-lizenz-toggle">
            <input type="checkbox" checked={inviteForm.lizenz} onChange={(e) => setInviteForm({ ...inviteForm, lizenz: e.target.checked })} className="h-4 w-4 accent-[#C5A059]" />
            Mit dauerhafter Voll-Lizenz einladen (kein Abo nötig)
          </label>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Gültig (Tage)</label>
              <Input data-testid="invite-days" type="number" min="0" value={inviteForm.days} onChange={(e) => setInviteForm({ ...inviteForm, days: e.target.value })} placeholder="∞" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Max. Nutzungen</label>
              <Input data-testid="invite-maxuses" type="number" min="0" value={inviteForm.max_uses} onChange={(e) => setInviteForm({ ...inviteForm, max_uses: e.target.value })} placeholder="∞" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <button data-testid="invite-create-btn" onClick={createCode} className="flex h-10 shrink-0 items-center gap-1 rounded-full bg-[#0A1128] px-3 text-sm font-semibold text-white active:scale-[0.98]">
              <Plus size={15} weight="bold" /> Code
            </button>
          </div>
          <div className="max-h-40 space-y-2 overflow-y-auto">
            {codes.length === 0 && <p className="py-2 text-center text-xs text-[#A0AEC0]">Noch keine Codes erstellt.</p>}
            {codes.map((c) => {
              const abgelaufen = c.expires_at && new Date(c.expires_at) < new Date();
              const erschoepft = c.max_uses > 0 && c.used_count >= c.max_uses;
              return (
                <div key={c.id} className="rounded-lg border border-[#E2E8F0] bg-white p-2.5" data-testid="invite-row">
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Code kopiert."); }} className="min-w-0 text-left" data-testid="invite-copy">
                      <p className={`font-mono text-base font-bold tracking-widest ${abgelaufen || erschoepft ? "text-[#A0AEC0] line-through" : "text-[#0A1128]"}`}>{c.code}</p>
                      <p className="text-[11px] text-[#718096]">
                        {c.used_count}{c.max_uses ? `/${c.max_uses}` : ""} genutzt{c.expires_at ? ` · bis ${new Date(c.expires_at).toLocaleDateString("de-DE")}` : " · unbegrenzt"}
                      </p>
                      {c.branche && <span className="mt-0.5 inline-block rounded bg-[#C5A059]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a6d2f]" data-testid="invite-branche-label">{branchen.find((x) => x.key === c.branche)?.label || c.branche}</span>}
                      {c.rolle === "admin" && <span className="mt-0.5 ml-1 inline-block rounded bg-[#0A1128] px-1.5 py-0.5 text-[10px] font-semibold text-white" data-testid="invite-rolle-label">Firmen-Admin</span>}
                      {c.frei_tage ? <span className="mt-0.5 ml-1 inline-block rounded bg-[#276749] px-1.5 py-0.5 text-[10px] font-semibold text-white" data-testid="invite-freitage-label">{c.frei_tage} Gratis-Tage</span> : null}
                    </button>
                    <button onClick={() => deleteCode(c.id)} data-testid="invite-delete" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#C0392B] hover:bg-[#C0392B]/10">
                      <Trash size={16} />
                    </button>
                  </div>
                  {c.registrations?.length > 0 && (
                    <div className="mt-2 space-y-1 border-t border-[#E2E8F0] pt-2" data-testid="invite-registrations">
                      {c.registrations.map((r, i) => (
                        <p key={i} className="truncate text-[11px] text-[#4A5568]" data-testid="invite-registration">
                          <span className="font-semibold">{r.name || r.email}</span>{r.name && r.email ? ` · ${r.email}` : ""}{r.registered_at ? ` · ${new Date(r.registered_at).toLocaleDateString("de-DE")}` : ""}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-2 space-y-3 rounded-xl bg-[#F8F9FA] p-4" data-testid="lizenz-section">
          <div className="flex items-center gap-2">
            <Key size={18} weight="duotone" color="#C5A059" />
            <p className="text-sm font-semibold text-[#0A1128]">Voll-Lizenz-Codes</p>
            <InfoHint testid="hint-lizenz-codes" text="Erzeuge hier Freischalt-Codes, die du nach einer PayPal-Zahlung an den Nutzer schickst. Der Nutzer löst den Code in seinem Profil ein und wird dauerhaft (oder für X Tage) freigeschaltet." />
          </div>
          <div className="flex gap-2">
            <button type="button" data-testid="liz-typ-lifetime" onClick={() => setLizForm({ ...lizForm, typ: "lifetime" })} className={`h-9 flex-1 rounded-full text-xs font-semibold ${lizForm.typ === "lifetime" ? "bg-[#0A1128] text-white" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Dauerhaft</button>
            <button type="button" data-testid="liz-typ-days" onClick={() => setLizForm({ ...lizForm, typ: "days" })} className={`h-9 flex-1 rounded-full text-xs font-semibold ${lizForm.typ === "days" ? "bg-[#C5A059] text-[#0A1128]" : "border border-[#E2E8F0] bg-white text-[#718096]"}`}>Auf Zeit</button>
          </div>
          {lizForm.typ === "days" && (
            <Input data-testid="liz-days" type="number" min="1" value={lizForm.days} onChange={(e) => setLizForm({ ...lizForm, days: e.target.value })} placeholder="Anzahl Tage – z. B. 365" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
          )}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Max. Einlösungen</label>
              <Input data-testid="liz-maxuses" type="number" min="0" value={lizForm.max_uses} onChange={(e) => setLizForm({ ...lizForm, max_uses: e.target.value })} placeholder="∞" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <div className="flex-[2]">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#A0AEC0]">Notiz (optional)</label>
              <Input data-testid="liz-note" value={lizForm.note} onChange={(e) => setLizForm({ ...lizForm, note: e.target.value })} placeholder="z. B. Max Mustermann" className="h-10 rounded-lg border-[#E2E8F0] bg-white text-sm" />
            </div>
            <button data-testid="liz-create-btn" onClick={createLizCode} className="flex h-10 shrink-0 items-center gap-1 rounded-full bg-[#0A1128] px-3 text-sm font-semibold text-white active:scale-[0.98]"><Plus size={15} weight="bold" /> Code</button>
          </div>
          <div className="max-h-40 space-y-2 overflow-y-auto">
            {lizCodes.length === 0 && <p className="py-2 text-center text-xs text-[#A0AEC0]">Noch keine Lizenz-Codes.</p>}
            {lizCodes.map((c) => {
              const erschoepft = c.max_uses > 0 && c.used_count >= c.max_uses;
              return (
                <div key={c.id} className="rounded-lg border border-[#E2E8F0] bg-white p-2.5" data-testid="liz-row">
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Code kopiert."); }} className="min-w-0 text-left" data-testid="liz-copy">
                      <p className={`font-mono text-base font-bold tracking-widest ${erschoepft ? "text-[#A0AEC0] line-through" : "text-[#0A1128]"}`}>{c.code}</p>
                      <p className="text-[11px] text-[#718096]">{c.typ === "days" ? `${c.days} Tage` : "Dauerhaft"} · {c.used_count}{c.max_uses ? `/${c.max_uses}` : ""} eingelöst{c.note ? ` · ${c.note}` : ""}</p>
                    </button>
                    <button onClick={() => deleteLizCode(c.id)} data-testid="liz-delete" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#C0392B] hover:bg-[#C0392B]/10"><Trash size={16} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
